import { buildOnboardingTrialProfile } from "@/components/Pricing/onboardingTrialProfile";
import wordmark from "@/assets/images/onboarding/Bustly.svg";
import bustlyAvatar from "@/assets/images/onboarding/Bustly_.png";
import idCard from "@/assets/images/onboarding/ID_card.svg";
import bytedanceModelIcon from "@/assets/images/pricing-models/bytedance.png";
import claudeModelIcon from "@/assets/images/pricing-models/claude.svg";
import nanoBananaModelIcon from "@/assets/images/pricing-models/nano-banana.svg";
import PricingCard, { type PlanData } from "../Pricing/PricingCard";
import {
  getPricingTierForPlanCode,
  getPricingTierPrice,
  resolveConfiguredPlanEstimatedOperations,
  resolveConfiguredPlanWorkUnits,
} from "../Pricing/pricingTierOptions";
import {
  buildInitialOnboardingWorkspacePayload,
  getVisibleOnboardingStepIndex,
  getVisibleOnboardingSteps,
  hasOnboardingStoreConnectSkipTrace,
  buildOnboardingWorkspaceUpdatePayload,
  hasReusableOnboardingWorkspace,
  removeConnectedIntegrationTrialRequirement,
  resolveAggregatedOnboardingTrialEligibilityState,
  resolveConnectedStoreProfile,
  resolveIntentSubmissionWorkspaceState,
  shouldAllowOnboardingStoreConnectSkip,
  updateConnectedIntegrationTrialRequirements,
} from "@/components/Onboarding/onboardingFlow";
import {
  buildCheckoutReturnUrls,
  buildOnboardingAdminSuccessRoute,
} from "../Pricing/paymentUrlUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useToast } from "@/contexts/ToastContext";
import {
  useWorkspaceIntegrations,
  type UseWorkspaceIntegrationsReturn,
} from "@/contexts/AuthContext/useWorkspaceIntegrations";
import { buildShopifyOnboardingConnectRedirectUrl } from "@/contexts/AuthContext/shopifyOnboardingResume";
import { billingService, type Plan } from "@common/api/billingService";
import { type Translate as TranslateFn, useTranslation } from '@client-app/i18n';
import {
  featureService,
  InviteStatusResponse,
  WorkspaceInvitation,
} from "@common/api/featureService";
import type { StoreTrialEligibilityState } from "@common/api/authService";
import { Tooltip } from "@common/index";
import {
  ArrowRight,
  Check,
  CircleNotch,
  SignOut,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import InviteSelection from "./InviteSelection";
import RepeatedStoreNotice, {
  type RepeatedStoreNoticeContent,
} from "./RepeatedStoreNotice";
import Modal from "../Settings/components/Modal";
import IntegrationApiKeyModal from "../Settings/components/integrations/IntegrationApiKeyModal";
import GoogleAdsAccountSelectionModal from "../Settings/components/integrations/GoogleAdsAccountSelectionModal";
import {
  getBrandIcon,
  getBrandIconFrameClassName,
  getBrandIconImageClassName,
} from "../Settings/components/integrations/IntegrationCard";
import {
  getIntegrationProviderForTool,
  normalizeIntegrationProvider,
  type ConnectableIntegrationScenario,
} from "../Settings/components/integrations/integrationCatalog";
import {
  normalizeConnectRestriction,
  type ConnectRestrictionPayload,
} from "../Settings/components/integrations/connectRestriction";
import StoreConnectionModal, {
  STORE_URL_INPUT_CONFIG,
} from "./StoreConnectionModal";
import { normalizeShopifyStoreDomainInput } from "./storeUrlNormalization";
import { useWorkspacePolling } from "./useWorkspacePolling";
import {
  resolveCurrentInviteEmail,
  shouldAutoAcceptInviteOnOnboarding,
  shouldEnterAcceptedInviteWorkspaceOnOnboarding,
  shouldProcessInviteTokenOnOnboarding,
} from "./inviteAcceptanceFlow";
import {
  buildInviteSuccessAdminRoute,
  resolveInviteAcceptWorkspaceId,
} from "./inviteReturn";
import {
  canContinueOnboardingConnectionStep,
  getOnboardingConnectionToolsForScenario,
  ONBOARDING_CONNECTION_CATEGORIES,
  resolveOnboardingConnectionRequirements,
  resolveOnboardingPlanStepAccess,
  resolveOnboardingStepForRendering,
  type OnboardingConnectionBrowseCategoryId,
  type OnboardingConnectionCategoryId,
} from "./connectionRequirements";
import { resolveOnboardingConnectionFeedback } from "./connectionFeedback";
import { resolveConnectedAppsDisplayState } from "./connectedAppsDisplay";
import {
  shouldReleaseOnboardingPlanLoading,
  type OnboardingCheckoutNavigationState,
} from "./planCheckoutNavigation";

const BUSTLY_AVATAR_SRC = bustlyAvatar;

export { normalizeShopifyStoreDomainInput } from "./storeUrlNormalization";

interface OnboardingIntegrationOption {
  id: string;
  provider: string;
  categoryId: OnboardingConnectionCategoryId;
  scenario: ConnectableIntegrationScenario;
}

const ONBOARDING_INTEGRATION_OPTIONS: OnboardingIntegrationOption[] =
  ONBOARDING_CONNECTION_CATEGORIES.flatMap((category) =>
    getOnboardingConnectionToolsForScenario(category.scenario).flatMap(
      (tool) => {
        const provider = getIntegrationProviderForTool(tool);
        return provider
          ? [{
              id: tool,
              provider,
              categoryId: category.id,
              scenario: category.scenario,
            }]
          : [];
      },
    ),
  );

const ONBOARDING_BROWSE_CATEGORIES: readonly {
  id: OnboardingConnectionBrowseCategoryId;
}[] = ONBOARDING_CONNECTION_CATEGORIES;

const ONBOARDING_CATEGORY_LABEL_KEYS: Record<
  OnboardingConnectionBrowseCategoryId,
  string
> = {
  store_ops: "website.onboarding.connect.categories.storeOps",
  advertising: "website.onboarding.connect.categories.advertising",
  email_marketing: "website.onboarding.connect.categories.emailMarketing",
  analytics: "website.onboarding.connect.categories.analytics",
  seo: "website.onboarding.connect.categories.seo",
  social_media: "website.onboarding.connect.categories.socialMedia",
  customers: "website.onboarding.connect.categories.customers",
};

const ONBOARDING_CATEGORY_DESCRIPTION_KEYS: Record<
  OnboardingConnectionBrowseCategoryId,
  string
> = {
  store_ops: "website.onboarding.connect.categories.descriptions.storeOps",
  advertising:
    "website.onboarding.connect.categories.descriptions.advertising",
  email_marketing:
    "website.onboarding.connect.categories.descriptions.emailMarketing",
  analytics: "website.onboarding.connect.categories.descriptions.analytics",
  seo: "website.onboarding.connect.categories.descriptions.seo",
  social_media:
    "website.onboarding.connect.categories.descriptions.socialMedia",
  customers: "website.onboarding.connect.categories.descriptions.customers",
};

const STORE_OPS_PROVIDER_KEYS = new Set(
  ONBOARDING_INTEGRATION_OPTIONS
    .filter((option) => option.categoryId === "store_ops")
    .map((option) => normalizeIntegrationProvider(option.provider)),
);

const MANUAL_CREDENTIAL_TOOLS = new Set([
  "Ahrefs",
  "Okendo",
  "Omnisend",
  "PrestaShop",
]);

const isStoreOpsProvider = (provider?: string | null) =>
  STORE_OPS_PROVIDER_KEYS.has(normalizeIntegrationProvider(provider));

const getConnectedOnboardingOptions = (
  integrations: readonly {
    provider?: string | null;
    connected?: boolean | null;
  }[],
) =>
  ONBOARDING_INTEGRATION_OPTIONS.filter((option) =>
    integrations.some(
      (integration) =>
        integration.connected === true &&
        normalizeIntegrationProvider(integration.provider) ===
          normalizeIntegrationProvider(option.provider),
    ),
  );

const buildFreeTrialUnavailableCopy = (
  t: TranslateFn,
  workspaceName?: string | null,
) => ({
  title: t('website.onboarding.notice.freeTrialUnavailableTitle'),
  description: t('website.onboarding.notice.freeTrialUnavailableDescription', {
    workspace_name:
      workspaceName || t('website.onboarding.notice.freeTrialUnavailableWorkspaceFallback'),
  }),
});

const extractWorkspaceName = (message?: string | null) => {
  if (!message) return null;
  const match = message.match(/workspace ['"]([^'"]+)['"]/i);
  return match ? match[1] : null;
};

const buildStoreLimitReachedCopy = (t: TranslateFn) => ({
  title: t('website.onboarding.notice.storeLimitReachedTitle'),
  description: t('website.onboarding.notice.storeLimitReachedDescription'),
});

const TRIAL_FALLBACK_WORK_UNITS = 400;
const WORK_UNIT_DISPLAY_MICROUSD = 100_000;

const toPositiveNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const resolveOnboardingTrialWorkUnits = (plans: readonly Plan[]): number => {
  const freePlan = plans.find(
    (plan) => String(plan.code || "").toUpperCase() === "FREE",
  );
  const rawFreePlan = freePlan as (Plan & Record<string, unknown>) | undefined;
  const displayBudgetMicrousd = toPositiveNumberOrNull(
    rawFreePlan?.displayBudgetMicrousdMonthly ??
      rawFreePlan?.display_budget_microusd_monthly ??
      rawFreePlan?.workUnitsBudgetDisplayMicrousdMonthly ??
      rawFreePlan?.work_units_budget_display_microusd_monthly,
  );

  if (displayBudgetMicrousd !== null) {
    return Math.floor(displayBudgetMicrousd / WORK_UNIT_DISPLAY_MICROUSD);
  }

  return TRIAL_FALLBACK_WORK_UNITS;
};

const V13AssistantHint: React.FC<{ lines: string[] }> = ({ lines }) => (
  <div className="mb-8 flex w-full items-center justify-center gap-3">
    <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#272729] shadow-sm ring-2 ring-white/80">
      <img
        src={BUSTLY_AVATAR_SRC}
        alt="Bustly"
        className="h-full w-full object-cover"
      />
    </div>
    <div className="rounded-[20px] border border-[#E0E3EC] bg-white px-5 py-3 text-left shadow-[0_12px_28px_rgba(26,22,47,0.06)]">
      <div className="flex items-center gap-3">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6E5BFF] opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1A162F]" />
        </span>
        <div className="text-[14px] font-medium leading-[1.35] text-[#1A162F] sm:whitespace-nowrap">
          {lines.join(" ")}
        </div>
      </div>
    </div>
  </div>
);

const getOnboardingMonthlyPlan = (plans: Plan[], planCode: string) => plans.find(
  (plan) => plan.code === planCode && plan.billingCycle === "monthly",
);

const buildPlanOptions = (
  t: TranslateFn,
  plans: Plan[] = [],
  locale = "en",
  trialWorkUnitsLabel = "",
) => {
  const numberFormatter = new Intl.NumberFormat(locale);
  const soloBackendPlan = getOnboardingMonthlyPlan(plans, "SOLO_199");
  const teamBackendPlan = getOnboardingMonthlyPlan(plans, "TEAM_1499");
  const soloCatalogTier = getPricingTierForPlanCode("solo", "SOLO_199");
  const teamCatalogTier = getPricingTierForPlanCode("team", "TEAM_1499");
  const soloWorkUnits = resolveConfiguredPlanWorkUnits(soloBackendPlan)
    ?? soloCatalogTier?.workUnits
    ?? 0;
  const teamWorkUnits = resolveConfiguredPlanWorkUnits(teamBackendPlan)
    ?? teamCatalogTier?.workUnits
    ?? 0;
  const soloEstimatedOperations = resolveConfiguredPlanEstimatedOperations(soloBackendPlan)
    ?? soloCatalogTier?.estimatedMonthlyOperations;
  const teamEstimatedOperations = resolveConfiguredPlanEstimatedOperations(teamBackendPlan)
    ?? teamCatalogTier?.estimatedMonthlyOperations;

  const sharedTrialDetails = {
    pricePrefix: t('website.onboarding.plan.startingPriceLabel'),
    priceSuffix: t('website.onboarding.plan.startingPriceSuffix'),
    pricePresentation: locale === "zh-CN"
      ? "leading-period" as const
      : "default" as const,
    trialBenefit: {
      value: trialWorkUnitsLabel,
    },
    workspaceGroupLabel: t('website.onboarding.plan.featureGroups.workspace'),
    builtInCapabilitiesLabel: t('website.onboarding.plan.featureGroups.builtInAiCapabilities'),
    showWorkUnitBenefitsInOnboarding: true,
    builtInModels: [
      {
        name: "Nano Banana 2",
        iconSrc: nanoBananaModelIcon,
        capabilityLabel: t('website.onboarding.plan.featureGroups.createImages'),
      },
      {
        name: "Seedance 2.0",
        iconSrc: bytedanceModelIcon,
        capabilityLabel: t('website.onboarding.plan.featureGroups.createVideos'),
      },
      {
        name: "Opus 4.8",
        iconSrc: claudeModelIcon,
        capabilityLabel: t('website.onboarding.plan.featureGroups.handleBusinessTasks'),
      },
    ],
  };

  return [
    {
      id: "Solo",
      planCode: "SOLO_199",
      title: t('website.onboarding.plan.solo.title'),
      pricingCard: {
        ...sharedTrialDetails,
        name: t('website.onboarding.plan.solo.title'),
        price: soloCatalogTier ? getPricingTierPrice(soloCatalogTier, "monthly") : 199,
        description: t('website.onboarding.plan.solo.description'),
        buttonText: t('website.onboarding.plan.solo.cta'),
        featureMode: "grouped",
        featureGroups: {
          workspace: [
            { id: "workspace-users", label: t('website.onboarding.plan.solo.lowestTierBullet1') },
            { id: "store-connections", label: t('website.onboarding.plan.solo.lowestTierBullet2') },
          ],
          workUnits: [
            {
              id: "monthly-work-units",
              label: t('website.onboarding.plan.solo.lowestTierBullet3', {
                value: numberFormatter.format(soloWorkUnits),
              }),
            },
            { id: "work-unit-top-ups", label: t('website.onboarding.plan.featureGroups.workUnitTopUps') },
          ],
          trainingAndIntegrations: [],
        },
        estimatedCapacity: soloEstimatedOperations
          ? t('website.onboarding.plan.solo.lowestTierCapacity', {
              min: numberFormatter.format(soloEstimatedOperations.min),
              max: numberFormatter.format(soloEstimatedOperations.max),
            })
          : undefined,
      } satisfies PlanData,
    },
    {
      id: "Team",
      planCode: "TEAM_1499",
      title: t('website.onboarding.plan.team.title'),
      pricingCard: {
        ...sharedTrialDetails,
        name: t('website.onboarding.plan.team.title'),
        price: teamCatalogTier ? getPricingTierPrice(teamCatalogTier, "monthly") : 1499,
        description: t('website.onboarding.plan.team.description'),
        buttonText: t('website.onboarding.plan.team.cta'),
        featureMode: "grouped",
        featureGroups: {
          workspace: [
            { id: "workspace-users", label: t('website.onboarding.plan.team.lowestTierBullet1') },
            { id: "store-connections", label: t('website.onboarding.plan.team.lowestTierBullet2') },
          ],
          workUnits: [
            {
              id: "monthly-work-units",
              label: t('website.onboarding.plan.team.lowestTierBullet3', {
                value: numberFormatter.format(teamWorkUnits),
              }),
            },
            { id: "work-unit-top-ups", label: t('website.onboarding.plan.featureGroups.workUnitTopUps') },
          ],
          trainingAndIntegrations: [],
        },
        estimatedCapacity: teamEstimatedOperations
          ? t('website.onboarding.plan.team.lowestTierCapacity', {
              min: numberFormatter.format(teamEstimatedOperations.min),
              max: numberFormatter.format(teamEstimatedOperations.max),
            })
          : undefined,
        highlightLimits: true,
      } satisfies PlanData,
    },
  ];
};

type OnboardingPlanOption = ReturnType<typeof buildPlanOptions>[number];

const buildIntentOptions = (t: TranslateFn) => [
  {
    id: "ads",
    label: t('website.onboarding.intent.options.ads.label'),
    desc: t('website.onboarding.intent.options.ads.description'),
  },
  {
    id: "conversion",
    label: t('website.onboarding.intent.options.conversion.label'),
    desc: t('website.onboarding.intent.options.conversion.description'),
  },
  {
    id: "retention",
    label: t('website.onboarding.intent.options.retention.label'),
    desc: t('website.onboarding.intent.options.retention.description'),
  },
  {
    id: "execution",
    label: t('website.onboarding.intent.options.execution.label'),
    desc: t('website.onboarding.intent.options.execution.description'),
  },
  { id: "other", label: t('website.onboarding.intent.options.other.label'), desc: "" },
];

const buildExpiryText = (daysUntilExpiry: number | undefined, t: TranslateFn) => {
  if (typeof daysUntilExpiry !== "number") {
    return null;
  }

  if (daysUntilExpiry < 0) {
    return t('website.onboarding.invite.expired');
  }

  if (daysUntilExpiry === 0) {
    return t('website.onboarding.invite.expiresToday');
  }

  if (daysUntilExpiry === 1) {
    return t('website.onboarding.invite.expiresInOneDay');
  }

  return t('website.onboarding.invite.expiresInDays', { days: daysUntilExpiry });
};

const normalizeEmail = (email?: string | null) =>
  (email || "").trim().toLowerCase();

const createUpdatedConnectionList = (current: string[], label: string) =>
  Array.from(new Set([...(current || []), label]));

const matchesIntegrationProvider = (
  integrationProvider: string | undefined,
  expectedProvider: string,
) =>
  normalizeIntegrationProvider(integrationProvider) ===
  normalizeIntegrationProvider(expectedProvider);

const INTEGRATION_ELIGIBILITY_PROVIDERS = STORE_OPS_PROVIDER_KEYS;

const FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES = new Set<
  StoreTrialEligibilityState
>(["already_bound", "trial_valid", "trial_expired"]);

const mergeEligibilityPayload = (
  latest?: ConnectRestrictionPayload | null,
  fallback?: ConnectRestrictionPayload | null,
): ConnectRestrictionPayload | undefined => {
  if (!latest) {
    return fallback ?? undefined;
  }

  if (!fallback) {
    return latest;
  }

  return {
    restrictionState:
      latest.restrictionState ??
      latest.eligibilityState ??
      fallback.restrictionState ??
      fallback.eligibilityState,
    eligibilityState: latest.eligibilityState ?? fallback.eligibilityState,
    blockConnect: latest.blockConnect ?? fallback.blockConnect,
    upgradeRequired: latest.upgradeRequired ?? fallback.upgradeRequired,
    message:
      latest.message ??
      latest.eligibilityMessage ??
      fallback.message ??
      fallback.eligibilityMessage,
    eligibilityMessage:
      latest.eligibilityMessage ?? fallback.eligibilityMessage,
    allowsFreshTrial: latest.allowsFreshTrial ?? fallback.allowsFreshTrial,
    trialRemainingDays:
      latest.trialRemainingDays ?? fallback.trialRemainingDays,
    currentWorkspaceId:
      latest.currentWorkspaceId ?? fallback.currentWorkspaceId,
    currentWorkspaceName:
      latest.currentWorkspaceName ?? fallback.currentWorkspaceName,
    currentStoreCount: latest.currentStoreCount ?? fallback.currentStoreCount,
    storeLimit: latest.storeLimit ?? fallback.storeLimit,
    upgradeTarget: latest.upgradeTarget ?? fallback.upgradeTarget,
  };
};

const buildFallbackStoreName = (platformId: string, rawStoreUrl: string) => {
  const normalizedStoreUrl = rawStoreUrl.trim();

  if (!normalizedStoreUrl) {
    return "";
  }

  if (platformId === "Shopify") {
    return `${normalizedStoreUrl}.myshopify.com`;
  }

  if (platformId === "BigCommerce") {
    return `store-${normalizedStoreUrl}.mybigcommerce.com`;
  }

  return normalizedStoreUrl;
};

const shouldShowConnectionSuccessToast = (result?: {
  restrictionState?: StoreTrialEligibilityState;
  blockConnect?: boolean;
  upgradeRequired?: boolean;
  message?: string | null;
  eligibilityState?: StoreTrialEligibilityState;
  allowsFreshTrial?: boolean;
  trialRemainingDays?: number | null;
  currentWorkspaceName?: string | null;
}) => {
  const restriction = normalizeConnectRestriction(result);

  return (
    !restriction.restrictionState || restriction.restrictionState === "eligible"
  );
};

const hasEligibilityPayloadMetadata = (
  payload?: ConnectRestrictionPayload | null,
) =>
  Boolean(
    payload &&
      (payload.restrictionState ||
        payload.eligibilityState ||
        payload.blockConnect ||
        payload.upgradeRequired ||
        payload.allowsFreshTrial === false ||
        payload.message ||
        payload.eligibilityMessage ||
        payload.currentWorkspaceId ||
        payload.currentWorkspaceName ||
        typeof payload.trialRemainingDays === "number"),
  );

const buildRepeatedStoreNoticeContent = (restriction: {
  restrictionState: StoreTrialEligibilityState | null;
  message: string | null;
  trialRemainingDays: number | null;
}, t: TranslateFn) => {
  const freeTrialUnavailableCopy = buildFreeTrialUnavailableCopy(t);
  const storeLimitReachedCopy = buildStoreLimitReachedCopy(t);
  if (
    restriction.restrictionState &&
    FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES.has(
      restriction.restrictionState,
    )
  ) {
    return freeTrialUnavailableCopy;
  }

  if (restriction.restrictionState === "store_limit_reached") {
    return {
      title: storeLimitReachedCopy.title,
      description: restriction.message || storeLimitReachedCopy.description,
    };
  }

  return null;
};

const isFreeTrialUnavailableRestriction = (restriction: {
  restrictionState: StoreTrialEligibilityState | null;
  message: string | null;
  trialRemainingDays: number | null;
}) =>
  Boolean(
    restriction.restrictionState &&
      FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES.has(
        restriction.restrictionState,
      ),
  );

const inferConnectedTrialRestrictionState = (
  restriction: {
    allowsFreshTrial: boolean | null;
    currentWorkspaceId: string | null;
    message: string | null;
    restrictionState: StoreTrialEligibilityState | null;
    trialRemainingDays: number | null;
  },
  currentWorkspaceId?: string | null,
): StoreTrialEligibilityState | null => {
  if (restriction.restrictionState || restriction.allowsFreshTrial !== false) {
    return restriction.restrictionState;
  }

  const normalizedCurrentWorkspaceId = currentWorkspaceId?.trim() || null;
  const normalizedRestrictionWorkspaceId =
    restriction.currentWorkspaceId?.trim() || null;

  if (
    normalizedRestrictionWorkspaceId &&
    normalizedCurrentWorkspaceId &&
    normalizedRestrictionWorkspaceId !== normalizedCurrentWorkspaceId
  ) {
    return "already_bound";
  }

  if (
    restriction.message &&
    /already (?:connected|bound) to another workspace/i.test(
      restriction.message,
    )
  ) {
    return "already_bound";
  }

  if (
    typeof restriction.trialRemainingDays === "number" &&
    restriction.trialRemainingDays > 0
  ) {
    return "trial_valid";
  }

  return "trial_expired";
};

interface StepConnectIntegrationRowProps {
  tool: string;
  isConnected: boolean;
  isSyncing: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

const StepConnectIntegrationRow: React.FC<StepConnectIntegrationRowProps> =
  React.memo(({ tool, isConnected, isSyncing, onConnect, onDisconnect }) => {
    const { t } = useTranslation();

    const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isSyncing) {
        return;
      }

      if (isConnected) {
        onDisconnect();
        return;
      }

      onConnect();
    };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (isSyncing) return;
          if (isConnected) {
            onDisconnect();
          } else {
            onConnect();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (isSyncing) return;
            if (isConnected) {
              onDisconnect();
            } else {
              onConnect();
            }
          }
        }}
        className={clsx(
          "group flex min-h-[76px] w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3.5 transition-all duration-300",
          isConnected
            ? "cursor-default border-[#52C41A] bg-[#F4FBF4] shadow-sm"
            : "cursor-pointer border-[#E6E8F0] bg-white shadow-[0_4px_14px_rgba(26,22,47,0.04)] hover:border-[#1A162F] hover:shadow-[0_8px_22px_rgba(26,22,47,0.07)]",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className={clsx(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              getBrandIconFrameClassName(tool),
            )}
          >
            <img
              src={getBrandIcon(tool)}
              alt=""
              className={getBrandIconImageClassName(tool, "h-6 w-6")}
              onError={(event) => {
                (event.target as HTMLImageElement).src =
                  "https://cdn.brandfetch.io/google.com/icon";
              }}
            />
          </div>
          <span
            className="min-w-0 break-words text-[15px] font-bold leading-tight text-[#1A162F]"
            title={tool}
          >
            {tool}
          </span>
        </div>

        <button
          type="button"
          onClick={handleActionClick}
          disabled={isSyncing}
          aria-label={t(
            isConnected
              ? "website.onboarding.connect.disconnectToolAction"
              : "website.onboarding.connect.connectToolAction",
            { tool },
          )}
          aria-busy={isSyncing}
          className={clsx(
            "group/btn flex min-w-[92px] shrink-0 items-center justify-center rounded-full border px-4 py-2 text-[13px] font-bold transition-all",
            isConnected
              ? "border-transparent bg-transparent pr-1 hover:bg-red-50 hover:text-red-600"
              : "border-[#E6E8F0] bg-white text-[#1A162F] group-hover:bg-[#1A162F] group-hover:border-[#1A162F] group-hover:text-white hover:bg-[#1A162F] hover:border-[#1A162F] hover:text-white",
          )}
        >
          {isSyncing ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : isConnected ? (
            <div className="flex items-center gap-1.5">
              <Check
                size={14}
                weight="bold"
                className="text-[#52C41A] group-hover/btn:hidden"
              />
              <span className="text-[#52C41A] group-hover/btn:hidden">
                {t('website.onboarding.connect.connected')}
              </span>
              <span className="hidden group-hover/btn:block text-red-600">
                {t('website.onboarding.connect.disconnect')}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span>{t('website.onboarding.connect.connect')}</span>
            </div>
          )}
        </button>
      </div>
    );
  });

const InviteStateCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <div className="w-full max-w-lg px-6">
    <div className="rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
        {icon}
      </div>
      <h1 className="mb-3 text-2xl font-bold text-text-main">{title}</h1>
      <div className="space-y-2 leading-relaxed text-text-sub">
        {description}
      </div>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  </div>
);

const formatInviteRoleName = (
  role: string | null | undefined,
  t: TranslateFn,
) => {
  const trimmedRole = String(role || "").trim();

  if (!trimmedRole) {
    return t('website.membersSettings.memberRole');
  }

  const normalized = trimmedRole.toUpperCase();
  if (normalized === "OWNER") return t('website.membersSettings.ownerRole');
  if (normalized === "ADMIN") return t('website.membersSettings.adminRole');
  if (normalized === "MEMBER") return t('website.membersSettings.memberRole');

  if (
    trimmedRole === trimmedRole.toUpperCase() ||
    trimmedRole === trimmedRole.toLowerCase()
  ) {
    return trimmedRole
      .replace(/[_-]+/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
      .join(" ");
  }

  return trimmedRole;
};

const getInviteRoleArticle = (roleName: string) => (
  /^[aeiou]/i.test(roleName.trim()) ? "an" : "a"
);

const getWorkspaceInitial = (workspaceName?: string | null) => {
  const trimmedName = String(workspaceName || "").trim();
  return trimmedName ? trimmedName.charAt(0).toUpperCase() : "W";
};

const InviteDifferentAccountCard: React.FC<{
  inviteStatus: InviteStatusResponse;
  currentEmail: string | null;
  onSwitchAccount: () => void;
}> = ({ inviteStatus, currentEmail, onSwitchAccount }) => {
  const { t } = useTranslation();
  const workspaceName =
    inviteStatus.workspaceName || t('website.onboarding.invite.thisWorkspace');
  const roleName = formatInviteRoleName(
    inviteStatus.dataRoleName ||
      inviteStatus.data_role_name ||
      inviteStatus.workspaceRoleName ||
      inviteStatus.workspaceRoleKey ||
      inviteStatus.role,
    t,
  );
  const inviterName =
    inviteStatus.inviterName ||
    inviteStatus.inviterEmail ||
    t('website.onboarding.invite.teammate');

  return (
    <div className="w-full max-w-[900px] px-6">
      <div className="mx-auto flex w-full flex-col items-center text-center">
        <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-[24px] border border-[#E0DFE8] bg-white text-[30px] font-bold text-[#1A162F] shadow-[0_4px_10px_rgba(26,22,47,0.08)]">
          {getWorkspaceInitial(workspaceName)}
        </div>

        <h1 className="max-w-[760px] text-[32px] font-bold leading-[1.15] text-[#1A162F] sm:text-[40px]">
          {t('website.onboarding.invite.joinWorkspaceTitle', {
            workspace: workspaceName,
          })}
        </h1>
        <p className="mt-7 max-w-[760px] text-[19px] leading-[1.45] text-[#676274] sm:text-[22px]">
          {t('website.onboarding.invite.singleSubtitle', {
            inviter: inviterName,
            article: getInviteRoleArticle(roleName),
            role: roleName,
          })}
        </p>
        <p className="mt-5 text-[18px] leading-snug text-[#8B8596] sm:text-[20px]">
          {t('website.onboarding.invite.sentTo')}{" "}
          <span className="font-bold text-[#1A162F]">
            {inviteStatus.invitedEmail}
          </span>
        </p>

        <div className="mt-8 flex w-full items-start gap-4 rounded-[18px] border border-red-200 bg-red-50 px-6 py-5 text-left text-red-700">
          <WarningCircle size={24} weight="bold" className="mt-0.5 shrink-0" />
          <div className="space-y-1 text-[18px] leading-[1.35] sm:text-[20px]">
            <p className="font-semibold">
              {t('website.onboarding.invite.differentAccountTitle')}
            </p>
            <p>
              {t('website.onboarding.invite.switchAccountDescription', {
                workspace: workspaceName,
              })}
            </p>
            {currentEmail ? (
              <p className="text-[15px] leading-relaxed text-red-600/80 sm:text-[16px]">
                {t('website.onboarding.invite.signedInAs')}{" "}
                <span className="font-semibold">{currentEmail}</span>.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-8 w-full rounded-[20px] border border-[#E1E4EC] bg-white p-5 shadow-[0_8px_20px_rgba(26,22,47,0.08)]">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.9fr)] md:items-center">
            <div className="flex min-w-0 items-center gap-5 text-left">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border border-[#E0E3EC] bg-[#F4F5F8] text-[24px] font-bold text-[#1A162F]">
                {getWorkspaceInitial(workspaceName)}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[20px] font-bold leading-tight text-[#1A162F]">
                  {workspaceName}
                </div>
                <div className="mt-2 truncate text-[18px] leading-tight text-[#6F697A]">
                  {t('website.onboarding.invite.roleLabel')} {roleName}
                </div>
              </div>
            </div>

            <button
              onClick={onSwitchAccount}
              className="flex min-h-[56px] w-full items-center justify-center rounded-[22px] bg-[#1A162F] px-6 text-center text-[18px] font-bold leading-tight text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#241F3D] sm:text-[20px]"
            >
              {t('website.onboarding.invite.logOutAndSwitch')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface StepProps {
  onNext: (payload?: {
    intent?: string;
    otherIntent?: string;
  }) => void | Promise<void>;
  onBack?: () => void;
  data: any;
  updateData: (data: any) => void;
}

interface PendingConnectionRequest {
  platformId: string;
  provider: string;
}

const StepWelcome: React.FC<{
  onNext: () => void | Promise<void>;
  error: string | null;
  isLoading?: boolean;
}> = ({ onNext, error, isLoading = false }) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      <div className="fixed left-6 top-6 z-[101]">
        <img src={wordmark} alt="Bustly Logo" className="h-9 sm:h-10" />
      </div>

      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-center">
          <div className="mb-10 flex w-full max-w-[700px] flex-col items-center">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t('website.onboarding.welcome.title')}
            </h2>
            <p className="mt-4 max-w-[600px] text-[15px] leading-relaxed text-[#666F8D] lg:text-[16px]">
              {t('website.onboarding.welcome.description')}
            </p>
          </div>

          <div className="flex w-full flex-col items-center justify-center">
            <div className="relative flex min-h-[400px] w-full items-center justify-center">
              <div className="pointer-events-none absolute inset-x-10 top-16 h-40 origin-center scale-[1.7] rounded-full bg-[radial-gradient(circle,_rgba(112,127,255,0.18)_0%,_rgba(112,127,255,0)_72%)] blur-3xl" />
              <div className="pointer-events-none absolute inset-x-14 bottom-10 h-28 origin-center scale-[1.7] rounded-full bg-[radial-gradient(circle,_rgba(26,22,47,0.10)_0%,_rgba(26,22,47,0)_72%)] blur-2xl" />

              <div className="relative flex w-full max-w-[450px] origin-center perspective-[1000px] flex-col items-center justify-center scale-[1.1]">
                <div
                  className={`relative mt-2 h-[380px] w-[248px] rounded-2xl animate-in fade-in slide-in-from-bottom-12 zoom-in-90 cursor-default transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] ${isLoaded ? "group hover:-translate-y-4 hover:scale-[1.02] hover:-rotate-2" : ""}`}
                >
                  <img
                    src={idCard}
                    className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_24px_40px_rgba(26,22,47,0.16)] transition-all duration-700 ease-out group-hover:drop-shadow-[0_40px_60px_rgba(110,91,255,0.35)]"
                    alt="ID Card"
                  />

                  <div className="absolute left-1/2 top-[128px] z-10 flex w-[190px] -translate-x-1/2 flex-col items-center px-3 pb-2 text-center">
                    <div className="relative mb-4 flex h-[40px] w-[40px] shrink-0 items-center justify-center overflow-visible">
                      <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#272729] shadow-sm ring-2 ring-white/10 transition-transform duration-500 ease-out group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(110,91,255,0.4)]">
                        <img
                          src={BUSTLY_AVATAR_SRC}
                          alt="Bustly"
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="absolute left-[110%] top-[-24%] z-[60] rounded-xl rounded-bl-sm border border-[#D9D4F4] bg-[#F6F3FF]/95 px-2.5 py-1.5 shadow-[0_10px_20px_rgba(26,22,47,0.12)] backdrop-blur-sm animate-in fade-in slide-in-from-bottom-2 duration-700 delay-300 transition-all group-hover:-translate-y-1 group-hover:rotate-2">
                        <div className="flex items-start gap-1.5">
                          <span className="relative mt-1 flex h-1.5 w-1.5 shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6E5BFF] opacity-70" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#1A162F]" />
                          </span>
                          <div className="whitespace-nowrap text-left text-[8px] font-medium leading-[1.3] text-[#1A162F]">
	                            <p>{t('website.onboarding.welcome.greeting')}</p>
                            <p>{t('website.onboarding.welcome.avatarSubtitle')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 flex w-full flex-col items-center text-center">
                      <h3 className="flex min-h-[22px] w-full items-center justify-center truncate whitespace-nowrap px-1 text-[16px] font-medium leading-[1.1] text-white">
                        Bustly
                      </h3>
                      <p className="mt-2 text-[12px] font-medium leading-none text-[#B8BDD4]">
                        -
                      </p>
                      <p className="mt-2 text-[12px] font-medium leading-[1.1] text-white">
                        {t('website.onboarding.welcome.avatarRole')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center">
            <button
              onClick={() => {
                void onNext();
              }}
              disabled={isLoading}
              className={clsx(
                "flex items-center gap-2 rounded-full px-8 py-3.5 text-[15px] font-bold text-white transition-all",
                isLoading
                  ? "cursor-not-allowed bg-gray-200 text-gray-500 shadow-none"
                  : "bg-[#1A162F] shadow-lg hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl",
              )}
            >
              {isLoading ? (
                <CircleNotch
                  size={18}
                  weight="bold"
                  className="animate-spin"
                />
              ) : null}
              {t('website.onboarding.welcome.cta')}
              {!isLoading ? <ArrowRight size={18} weight="bold" /> : null}
            </button>

            {error ? (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepIntent: React.FC<
  StepProps & { isLoading?: boolean; error?: string | null }
> = ({ onNext, onBack, data, updateData, isLoading = false, error = null }) => {
  const { t } = useTranslation();
  const intentOptions = useMemo(() => buildIntentOptions(t), [t]);
  const [selectedIntent, setSelectedIntent] = useState(data?.intent || null);
  const [otherText, setOtherText] = useState(data?.otherIntent || "");

  const handleSelect = (id: string) => {
    setSelectedIntent(id);
    updateData({ intent: id });
  };

  const canContinue = Boolean(
    selectedIntent && (selectedIntent !== "other" || otherText.trim() !== ""),
  );

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-left">
          <div className="mb-10 flex w-full max-w-[700px] flex-col items-center text-center">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t('website.onboarding.intent.title')}
            </h2>
            <p className="mt-4 max-w-[600px] text-[15px] leading-relaxed text-[#666F8D] lg:text-[16px]">
              {t('website.onboarding.intent.description')}
            </p>
          </div>

          <div className="flex w-full max-w-[540px] flex-col items-center">
            <V13AssistantHint
              lines={[t('website.onboarding.intent.hintLine1'), t('website.onboarding.intent.hintLine2')]}
            />

            <div className="flex w-full flex-col gap-3">
              {intentOptions.map((option) => {
                const isSelected = selectedIntent === option.id;
                const isOther = option.id === "other";

                return (
                  <div
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    className={clsx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-[18px] border px-4 py-3.5 text-left transition-all duration-300",
                      isSelected
                        ? "border-[#D9DFEC] bg-white shadow-[0_8px_22px_rgba(26,22,47,0.06)]"
                        : "border-[#E6E8F0] bg-white shadow-[0_4px_14px_rgba(26,22,47,0.04)] hover:border-[#1A162F] hover:shadow-[0_8px_22px_rgba(26,22,47,0.07)]",
                    )}
                  >
                    <div
                      className={clsx(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                        isSelected ? "border-[#1A162F]" : "border-gray-300",
                      )}
                    >
                      {isSelected ? (
                        <div className="h-2.5 w-2.5 rounded-full bg-[#1A162F]" />
                      ) : null}
                    </div>

                    <div className="relative flex min-w-0 flex-1 items-center">
                      {isOther ? (
                        <>
                          <span
                            className={clsx(
                              "pointer-events-none absolute left-0 block text-[15px] font-bold transition-all duration-300",
                              isSelected ? "text-[#1A162F]" : "text-[#1A162F]",
                              isSelected || otherText
                                ? "opacity-0 -translate-y-2"
                                : "opacity-100 translate-y-0",
                            )}
                          >
                            {option.label}
                          </span>
                          <input
                            type="text"
                            placeholder={
                              isSelected
                                ? t('website.onboarding.intent.otherPlaceholder')
                                : ""
                            }
                            value={otherText}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelect(option.id);
                            }}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setOtherText(nextValue);
                              updateData({ otherIntent: nextValue });
                              if (!isSelected) {
                                handleSelect(option.id);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && canContinue) {
                                void onNext({
                                  intent: selectedIntent || option.id,
                                  otherIntent: event.currentTarget.value.trim(),
                                });
                              }
                            }}
                            className={clsx(
                              "w-full appearance-none border-0 bg-transparent text-[15px] font-bold text-text-main caret-[#1A162F] shadow-none ring-0 transition-all duration-300 placeholder:font-normal placeholder:text-gray-400 outline-none focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none",
                              !isSelected && !otherText
                                ? "opacity-0 translate-y-2 pointer-events-none"
                                : "opacity-100 translate-y-0",
                            )}
                            style={{
                              WebkitAppearance: "none",
                              appearance: "none",
                              outline: "none",
                              boxShadow: "none",
                              border: "0",
                              borderRadius: 0,
                            }}
                          />
                        </>
                      ) : (
                        <div className="flex flex-col">
                          <span className="block text-[15px] font-bold text-[#1A162F]">
                            {option.label}
                          </span>
                          {option.desc ? (
                            <span
                              className={clsx(
                                "text-[13px] mt-0.5",
                                isSelected
                                  ? "text-[#666F8D]"
                                  : "text-[#8A93B2]",
                              )}
                            >
                              {option.desc}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex w-full items-center justify-between">
              <button
                onClick={onBack}
                className="flex min-w-[100px] items-center justify-center rounded-full border border-gray-200 bg-transparent px-6 py-3 text-[14px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-white hover:shadow"
              >
                {t('website.onboarding.common.back')}
              </button>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    void onNext({
                      intent: "skipped",
                      otherIntent: "",
                    });
                  }}
                  disabled={isLoading}
                  className="flex min-w-[100px] items-center justify-center rounded-full border border-transparent bg-transparent px-6 py-3 text-[14px] font-bold text-[#666F8D] transition-all hover:text-[#1A162F] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('website.onboarding.common.skip')}
                </button>
                <button
                  onClick={() => {
                    void onNext({
                      intent: selectedIntent || "",
                      otherIntent: otherText.trim(),
                    });
                  }}
                  disabled={!canContinue || isLoading}
                  className={clsx(
                    "flex items-center gap-2 rounded-full px-8 py-3 text-[15px] font-bold transition-all shadow-lg",
                    canContinue && !isLoading
                      ? "bg-[#1A162F] text-white hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl"
                      : "cursor-not-allowed bg-gray-100 text-gray-400 shadow-none",
                  )}
                >
                  {isLoading ? (
                    <CircleNotch
                      size={18}
                      weight="bold"
                      className="animate-spin"
                    />
                  ) : null}
                  {t('website.onboarding.intent.setPriority')}
                  {!isLoading ? <ArrowRight size={18} weight="bold" /> : null}
                </button>
              </div>
            </div>

            {error ? (
              <div className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepConnect: React.FC<
  StepProps & {
    isLoading?: boolean;
    isTrialUnavailableNoticeVisible: boolean;
    isTrialUnavailableNoticeDismissed: boolean;
    trialUnavailableNoticeContent: RepeatedStoreNoticeContent | null;
    dismissTrialUnavailableNotice: () => void;
    triggerTrialUnavailableNotice: (
      content?: RepeatedStoreNoticeContent | null,
    ) => void;
    pendingConnectionRequest: PendingConnectionRequest | null;
    setPendingConnectionRequest: React.Dispatch<
      React.SetStateAction<PendingConnectionRequest | null>
    >;
    workspaceReady?: boolean;
    workspaceError?: string | null;
    workspaceIntegrations: UseWorkspaceIntegrationsReturn;
  }
> = ({
  onNext,
  onBack,
  data,
  updateData,
  isLoading = false,
  isTrialUnavailableNoticeVisible,
  isTrialUnavailableNoticeDismissed,
  trialUnavailableNoticeContent,
  dismissTrialUnavailableNotice,
  triggerTrialUnavailableNotice,
  pendingConnectionRequest,
  setPendingConnectionRequest,
  workspaceReady = true,
  workspaceError = null,
  workspaceIntegrations,
}) => {
  const { t } = useTranslation();
  const { showToast, showActionToast } = useToast();
  const [showTrialUnavailableModal, setShowTrialUnavailableModal] = useState(false);
  const [trialUnavailableMessage, setTrialUnavailableMessage] = useState<string | null>(null);
  const [trialUnavailableWorkspaceName, setTrialUnavailableWorkspaceName] = useState<string | null>(null);
  const [trialUnavailablePlatformId, setTrialUnavailablePlatformId] =
    useState<string | null>(null);
  const [isTrialUnavailableContinuing, setIsTrialUnavailableContinuing] =
    useState(false);
  const [googleAdsAccountSelectionConnectionId, setGoogleAdsAccountSelectionConnectionId] =
    useState<string | null>(null);
  const platformOptions = ONBOARDING_INTEGRATION_OPTIONS;
  const connectingTool = data.storeConnectionPlatform || null;
  const [selectedCategory, setSelectedCategory] = useState<OnboardingConnectionBrowseCategoryId>(
    "store_ops",
  );
  const [disconnectingTool, setDisconnectingTool] = useState<string | null>(
    null,
  );
  const [disconnectingPlatformId, setDisconnectingPlatformId] = useState<
    string | null
  >(null);
  const storeUrl = data.storeConnectionStoreUrl || "";
  const showConnectionFeedbackToast = (error: unknown, platformId: string) => {
    const feedback = resolveOnboardingConnectionFeedback({
      error,
      fallbackMessage: t("website.onboarding.connect.connectFailed", {
        platform: platformId,
      }),
      notCompletedMessage: t(
        "website.onboarding.connect.connectionNotCompleted",
      ),
    });

    if (feedback.duration) {
      showActionToast({
        message: feedback.message,
        type: feedback.type,
        duration: feedback.duration,
      });
      return;
    }

    showToast(feedback.message, feedback.type);
  };
  const setStoreUrl = useCallback(
    (value: string) => {
      updateData({ storeConnectionStoreUrl: value });
    },
    [updateData],
  );
  const [manualCredentialPlatformId, setManualCredentialPlatformId] =
    useState<string | null>(null);
  const [manualCredentialApiKey, setManualCredentialApiKey] = useState("");
  const [manualCredentialStoreUrl, setManualCredentialStoreUrl] = useState("");
  const [manualCredentialAccountLabel, setManualCredentialAccountLabel] =
    useState("");
  const [syncingTools, setSyncingTools] = useState<Set<string>>(new Set());
  const bigCommerceStoreHash = data.storeConnectionStoreHash || "";
  const setBigCommerceStoreHash = useCallback(
    (value: string) => {
      updateData({ storeConnectionStoreHash: value });
    },
    [updateData],
  );
  const bigCommerceAccountUuid = data.storeConnectionAccountUuid || "";
  const setBigCommerceAccountUuid = useCallback(
    (value: string) => {
      updateData({ storeConnectionAccountUuid: value });
    },
    [updateData],
  );
  const urlConnectionInFlightRef = useRef(false);
  const manualCredentialConnectionInFlightRef = useRef(false);
  const connectedIntegrationsRef = useRef<string[]>(
    data.connectedIntegrations || [],
  );
  const trialRequirementsRef = useRef(
    data.connectedIntegrationTrialRequirements || {},
  );
  const initialIntegrationEligibilityKeysRef = useRef<Record<string, string>>({});
  const pendingEligibilityPayloadRef = useRef<
    Record<string, ConnectRestrictionPayload | undefined>
  >({});
  const lastServerConnectionSnapshotRef = useRef<string | null>(null);
  const googleAdsAccountSelectionSavedRef = useRef(false);
  const isStepConnectMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isStepConnectMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    connectedIntegrationsRef.current = data.connectedIntegrations || [];
  }, [data.connectedIntegrations]);

  useEffect(() => {
    trialRequirementsRef.current = data.connectedIntegrationTrialRequirements || {};
  }, [data.connectedIntegrationTrialRequirements]);

  const resetConnectionForm = useCallback(() => {
    updateData({
      storeConnectionPlatform: null,
      storeConnectionStoreUrl: "",
      storeConnectionStoreHash: "",
      storeConnectionAccountUuid: "",
    });
  }, [updateData]);

  const resetManualCredentialForm = useCallback(() => {
    setManualCredentialPlatformId(null);
    setManualCredentialApiKey("");
    setManualCredentialStoreUrl("");
    setManualCredentialAccountLabel("");
  }, []);

  const syncConnectedIntegrationMetadata = async (
    platformId: string,
    provider: string,
  ) => {
    const integrations = await workspaceIntegrations.refresh();
    const refreshedIntegration =
      integrations.find((integration) =>
        matchesIntegrationProvider(integration.provider, provider),
      ) || null;
    const nextConnectedIntegrations = createUpdatedConnectionList(
      connectedIntegrationsRef.current,
      platformId,
    );
    connectedIntegrationsRef.current = nextConnectedIntegrations;

    const nextData: Record<string, unknown> = {
      connectedIntegrations: nextConnectedIntegrations,
    };

    if (isStoreOpsProvider(provider)) {
      const { storeName, storeLogoUrl } = resolveConnectedStoreProfile(
        integrations,
        provider,
      );
      nextData.connectedStoreName = data.connectedStoreName || storeName;
      nextData.connectedStoreLogoUrl =
        data.connectedStoreLogoUrl || storeLogoUrl;
    }

    updateData(nextData);

    return refreshedIntegration;
  };

  useEffect(() => {
    if (
      !data.workspaceId ||
      !workspaceIntegrations.hasFetchedCurrentWorkspace
    ) {
      return;
    }

    const integrations = workspaceIntegrations.allIntegrations ?? [];
    const connectedOptions = getConnectedOnboardingOptions(integrations);
    const connectedToolNames = connectedOptions.map((option) => option.id);
    const firstConnectedStore = connectedOptions.find(
      (option) => option.categoryId === "store_ops",
    );
    const connectedStoreProfile = firstConnectedStore
      ? resolveConnectedStoreProfile(integrations, firstConnectedStore.provider)
      : { storeName: "", storeLogoUrl: null };
    const snapshotKey = JSON.stringify({
      connectedToolNames,
      connectedStoreName: connectedStoreProfile.storeName || "",
      connectedStoreLogoUrl: connectedStoreProfile.storeLogoUrl || null,
    });

    if (lastServerConnectionSnapshotRef.current === snapshotKey) {
      return;
    }

    lastServerConnectionSnapshotRef.current = snapshotKey;
    connectedIntegrationsRef.current = connectedToolNames;
    updateData({
      connectedIntegrations: connectedToolNames,
      connectedStoreName: connectedStoreProfile.storeName || "",
      connectedStoreLogoUrl: connectedStoreProfile.storeLogoUrl || null,
    });
  }, [
    data.workspaceId,
    updateData,
    workspaceIntegrations.allIntegrations,
    workspaceIntegrations.hasFetchedCurrentWorkspace,
  ]);

  const buildEligibilityPayloadFromIntegration = (integration?: {
    restrictionState?: StoreTrialEligibilityState | null;
    eligibilityState?: StoreTrialEligibilityState | null;
    blockConnect?: boolean | null;
    upgradeRequired?: boolean | null;
    allowsFreshTrial?: boolean | null;
    message?: string | null;
    eligibilityMessage?: string | null;
    currentWorkspaceId?: string | null;
    currentWorkspaceName?: string | null;
    trialRemainingDays?: number | null;
  } | null) => {
    if (!integration) {
      return undefined;
    }

    return {
      restrictionState: integration.restrictionState,
      eligibilityState: integration.eligibilityState,
      blockConnect: integration.blockConnect ?? undefined,
      upgradeRequired: integration.upgradeRequired ?? undefined,
      allowsFreshTrial: integration.allowsFreshTrial ?? undefined,
      message: integration.message ?? integration.eligibilityMessage,
      currentWorkspaceId: integration.currentWorkspaceId ?? undefined,
      currentWorkspaceName: integration.currentWorkspaceName ?? undefined,
      trialRemainingDays: integration.trialRemainingDays ?? undefined,
    };
  };

  const applyEligibilityFromConnection = (
    platformId: string,
    result?: ConnectRestrictionPayload,
    rawStoreUrl = storeUrl,
  ) => {
    const platform = platformOptions.find((option) => option.id === platformId);
    if (!platform || platform.categoryId !== "store_ops") {
      return false;
    }

    const normalizedRestriction = normalizeConnectRestriction(result);
    const effectiveRestrictionState =
      normalizedRestriction.restrictionState ??
      (connectedIntegrationsRef.current.includes(platformId)
        ? inferConnectedTrialRestrictionState(
            normalizedRestriction,
            data.workspaceId,
          )
        : null);
    const restriction = {
      ...normalizedRestriction,
      restrictionState: effectiveRestrictionState,
    };
    const nextTrialRequirements = updateConnectedIntegrationTrialRequirements(
      trialRequirementsRef.current,
      platformId,
      {
        restrictionState: restriction.restrictionState,
        blockConnect: restriction.blockConnect,
        allowsFreshTrial: restriction.allowsFreshTrial ?? undefined,
      },
    );
    trialRequirementsRef.current = nextTrialRequirements;

    if (
      restriction.restrictionState &&
      restriction.restrictionState !== "eligible"
    ) {
      const isFreeTrialUnavailable =
        isFreeTrialUnavailableRestriction(restriction);
      updateData({
        storeConnectionPlatform: null,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
        connectedIntegrationTrialRequirements: nextTrialRequirements,
        trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
          nextTrialRequirements,
        ),
        connectedStoreName:
          data.connectedStoreName ||
          buildFallbackStoreName(platformId, rawStoreUrl),
      });
      const noticeContent = buildRepeatedStoreNoticeContent(restriction, t);
      if (isFreeTrialUnavailable) {
        setTrialUnavailableMessage(restriction.message);
        setTrialUnavailableWorkspaceName(restriction.currentWorkspaceName);
        setTrialUnavailablePlatformId(platformId);
        setShowTrialUnavailableModal(true);
      } else if (noticeContent) {
        triggerTrialUnavailableNotice(noticeContent);
      }
      return true;
    }

    updateData({
      connectedIntegrationTrialRequirements: nextTrialRequirements,
      trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
        nextTrialRequirements,
      ),
    });
    return false;
  };

  useEffect(() => {
    const integrations = workspaceIntegrations.allIntegrations ?? [];
    if (integrations.length === 0) {
      return;
    }

    platformOptions.forEach((platform) => {
      if (!INTEGRATION_ELIGIBILITY_PROVIDERS.has(platform.provider)) {
        return;
      }

      const integration = integrations.find(
        (item) =>
          item.connected &&
          matchesIntegrationProvider(item.provider, platform.provider),
      );
      const eligibilityPayload =
        buildEligibilityPayloadFromIntegration(integration);
      if (!hasEligibilityPayloadMetadata(eligibilityPayload)) {
        return;
      }

      const eligibilityKey = JSON.stringify(eligibilityPayload ?? {});
      if (
        initialIntegrationEligibilityKeysRef.current[platform.id] ===
        eligibilityKey
      ) {
        return;
      }

      initialIntegrationEligibilityKeysRef.current[platform.id] = eligibilityKey;
      if (trialRequirementsRef.current?.[platform.id] === "checkout_required") {
        return;
      }

      applyEligibilityFromConnection(platform.id, eligibilityPayload as any);
    });
  }, [platformOptions, workspaceIntegrations.allIntegrations]);

  const startConnectionFlow = async (
    platformId: string,
    provider: string,
    options?: {
      skipSyncingGuard?: boolean;
      connectionConfig?: Record<string, unknown>;
    },
  ) => {
    if (
      (syncingTools.has(platformId) && !options?.skipSyncingGuard) ||
      workspaceIntegrations.isConnected(provider)
    ) {
      return;
    }

    if (STORE_URL_INPUT_CONFIG[platformId]) {
      updateData({
        storeConnectionPlatform: platformId,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
      });
      return;
    }

    if (MANUAL_CREDENTIAL_TOOLS.has(platformId) && !options?.connectionConfig) {
      setManualCredentialPlatformId(platformId);
      setManualCredentialApiKey("");
      setManualCredentialStoreUrl("");
      setManualCredentialAccountLabel("");
      return;
    }

    const usesStoreTrialEligibility = isStoreOpsProvider(provider);
    const clearSyncingState = () => {
      setSyncingTools((previous) => {
        const next = new Set(previous);
        next.delete(platformId);
        return next;
      });
      delete pendingEligibilityPayloadRef.current[platformId];
    };
    const resetManualCredentialFormAfterSuccess = () => {
      if (MANUAL_CREDENTIAL_TOOLS.has(platformId)) {
        resetManualCredentialForm();
      }
    };
    const handleNonStoreRestriction = (
      payload?: (ConnectRestrictionPayload & { success?: boolean }) | null,
    ) => {
      const restriction = normalizeConnectRestriction(payload);
      if (
        payload?.success !== false &&
        !restriction.blockConnect &&
        !restriction.upgradeRequired
      ) {
        return false;
      }

      showToast(
        restriction.message ||
          t("website.onboarding.connect.connectFailed", {
            platform: platformId,
          }),
        "error",
      );
      return true;
    };

    try {
      setSyncingTools((prev) => new Set(prev).add(platformId));
      const result = await workspaceIntegrations.connect(
        provider,
        {
          connection_scene: "onboarding",
          ...(options?.connectionConfig || {}),
        },
        {
          onSuccess: async () => {
            const refreshedIntegration =
              await syncConnectedIntegrationMetadata(platformId, provider);
            const refreshedEligibilityPayload = usesStoreTrialEligibility
              ? buildEligibilityPayloadFromIntegration(refreshedIntegration)
              : undefined;
            const eligibilityPayload = mergeEligibilityPayload(
              refreshedEligibilityPayload,
              pendingEligibilityPayloadRef.current[platformId],
            );
            clearSyncingState();
            resetManualCredentialFormAfterSuccess();
            if (
              usesStoreTrialEligibility &&
              applyEligibilityFromConnection(
                platformId,
                eligibilityPayload as any,
              )
            ) {
              return;
            }
            showToast(
              t("website.onboarding.connect.connectedSuccess", {
                platform: platformId,
              }),
              "success",
            );
          },
          onError: (message: string) => {
            clearSyncingState();
            showConnectionFeedbackToast(message, platformId);
          },
          onRestriction: (restriction) => {
            clearSyncingState();
            if (usesStoreTrialEligibility) {
              applyEligibilityFromConnection(platformId, restriction);
              return;
            }
            handleNonStoreRestriction(restriction);
          },
        },
      );
      pendingEligibilityPayloadRef.current[platformId] = result;

      const manualSubmittedStoreUrl =
        typeof options?.connectionConfig?.storeUrl === "string"
          ? options.connectionConfig.storeUrl
          : storeUrl;

      if (
        usesStoreTrialEligibility &&
        applyEligibilityFromConnection(
          platformId,
          result,
          manualSubmittedStoreUrl,
        )
      ) {
        clearSyncingState();
        resetManualCredentialFormAfterSuccess();
        return;
      }

      if (!usesStoreTrialEligibility && handleNonStoreRestriction(result)) {
        clearSyncingState();
        return;
      }

      if (result?.requiresAccountSelection) {
        clearSyncingState();
        if (!result.connectionId) {
          showToast(
            t("website.onboarding.connect.connectFailed", {
              platform: platformId,
            }),
            "error",
          );
          return;
        }
        setGoogleAdsAccountSelectionConnectionId(String(result.connectionId));
        return;
      }

      if (!result?.uiOpened) {
        const refreshedIntegration = await syncConnectedIntegrationMetadata(
          platformId,
          provider,
        );
        clearSyncingState();
        resetManualCredentialFormAfterSuccess();
        if (
          usesStoreTrialEligibility &&
          applyEligibilityFromConnection(
            platformId,
            buildEligibilityPayloadFromIntegration(refreshedIntegration) as any,
          )
        ) {
          return;
        }
        if (shouldShowConnectionSuccessToast(result)) {
          showToast(
            t('website.onboarding.connect.connectedSuccess', { platform: platformId }),
            "success",
          );
        }
      }
    } catch (error: any) {
      clearSyncingState();
      showConnectionFeedbackToast(error, platformId);
    }
  };

  const queueConnectionUntilWorkspaceReady = (
    platformId: string,
    provider: string,
  ) => {
    setPendingConnectionRequest({ platformId, provider });
    setSyncingTools((prev) => new Set(prev).add(platformId));
  };

  const handleConnect = async (platformId: string, provider: string) => {
    if (!workspaceReady) {
      queueConnectionUntilWorkspaceReady(platformId, provider);
      return;
    }

    await startConnectionFlow(platformId, provider);
  };

  const handleManualCredentialSubmit = async () => {
    if (!manualCredentialPlatformId || manualCredentialConnectionInFlightRef.current) {
      return;
    }

    const option = platformOptions.find(
      (platform) => platform.id === manualCredentialPlatformId,
    );
    if (!option) {
      return;
    }

    manualCredentialConnectionInFlightRef.current = true;
    try {
      await startConnectionFlow(manualCredentialPlatformId, option.provider, {
        connectionConfig: {
          apiKey: manualCredentialApiKey.trim(),
          storeUrl: manualCredentialStoreUrl.trim(),
          accountLabel: manualCredentialAccountLabel.trim() || undefined,
        },
      });
    } finally {
      manualCredentialConnectionInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!pendingConnectionRequest) {
      return;
    }

    if (workspaceError) {
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(pendingConnectionRequest.platformId);
        return next;
      });
      setPendingConnectionRequest(null);
      return;
    }

    if (!workspaceReady) {
      return;
    }

    const { platformId, provider } = pendingConnectionRequest;
    if (STORE_URL_INPUT_CONFIG[platformId]) {
      updateData({
        pendingConnectionRequest: null,
        storeConnectionPlatform: platformId,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
      });
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(platformId);
        return next;
      });
      return;
    }

    setPendingConnectionRequest(null);
    setSyncingTools((prev) => {
      const next = new Set(prev);
      next.delete(platformId);
      return next;
    });
    void startConnectionFlow(platformId, provider, {
      skipSyncingGuard: true,
    });
  }, [pendingConnectionRequest, workspaceError, workspaceReady]);

  const handleDisconnect = async (platformId: string, provider: string) => {
    if (disconnectingPlatformId) {
      return false;
    }

    setDisconnectingPlatformId(platformId);
    try {
      await workspaceIntegrations.disconnect(provider);
      const integrations = await workspaceIntegrations.refresh();
      const connectedOptions = getConnectedOnboardingOptions(integrations);
      const nextConnectedIntegrations = connectedOptions.map(
        (option) => option.id,
      );
      const nextTrialRequirements = removeConnectedIntegrationTrialRequirement(
        trialRequirementsRef.current,
        platformId,
      );
      const firstConnectedStore = connectedOptions.find(
        (option) => option.categoryId === "store_ops",
      );
      const nextConnectedStoreProfile = firstConnectedStore
        ? resolveConnectedStoreProfile(
            integrations,
            firstConnectedStore.provider,
          )
        : { storeName: "", storeLogoUrl: null };
      connectedIntegrationsRef.current = nextConnectedIntegrations;
      trialRequirementsRef.current = nextTrialRequirements;
      updateData({
        connectedIntegrations: nextConnectedIntegrations,
        connectedStoreName: nextConnectedStoreProfile.storeName || "",
        connectedStoreLogoUrl:
          nextConnectedStoreProfile.storeLogoUrl || null,
        connectedIntegrationTrialRequirements: nextTrialRequirements,
        trialEligibilityState:
          resolveAggregatedOnboardingTrialEligibilityState(
            nextTrialRequirements,
          ),
      });
      showToast(
        t('website.onboarding.connect.disconnectedSuccess', { platform: platformId }),
        "success",
      );
      setDisconnectingTool(null);
      return true;
    } catch (error: any) {
      showToast(
        error?.message || t('website.onboarding.connect.disconnectFailed', { platform: platformId }),
        "error",
      );
      return false;
    } finally {
      setDisconnectingPlatformId(null);
    }
  };

  const handleGoogleAdsAccountSelectionSaved = async () => {
    await syncConnectedIntegrationMetadata("Google Ads", "google-ads");
    googleAdsAccountSelectionSavedRef.current = true;
  };

  const handleGoogleAdsAccountSelectionClose = async () => {
    if (googleAdsAccountSelectionSavedRef.current) {
      googleAdsAccountSelectionSavedRef.current = false;
      setGoogleAdsAccountSelectionConnectionId(null);
      return;
    }

    const didDisconnect = await handleDisconnect("Google Ads", "google-ads");
    if (didDisconnect) {
      setGoogleAdsAccountSelectionConnectionId(null);
    }
  };

  const handleConnectionSubmit = async () => {
    if (!connectingTool) {
      return;
    }

    if (urlConnectionInFlightRef.current) {
      return;
    }

    const option = platformOptions.find(
      (platform) => platform.id === connectingTool,
    );
    if (!option) {
      return;
    }

    const activeConnectingTool = connectingTool;
    const submittedStoreUrl = storeUrl.trim();
    const submittedShopifyStoreDomain =
      normalizeShopifyStoreDomainInput(storeUrl);
    let keepSyncingUntilCallback = false;

    try {
      urlConnectionInFlightRef.current = true;
      setSyncingTools((prev) => new Set(prev).add(activeConnectingTool));

      const commonConnectOptions = {
        onSuccess: async () => {
          const refreshedIntegration = await syncConnectedIntegrationMetadata(
            activeConnectingTool,
            option.provider,
          );
          const refreshedEligibilityPayload =
            buildEligibilityPayloadFromIntegration(refreshedIntegration);
          const eligibilityPayload = INTEGRATION_ELIGIBILITY_PROVIDERS.has(
            option.provider,
          )
            ? refreshedEligibilityPayload
            : undefined;

          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });

          if (
            applyEligibilityFromConnection(
              activeConnectingTool,
              eligibilityPayload as any,
              submittedStoreUrl,
            )
          ) {
            return;
          }
          showToast(
            t('website.onboarding.connect.connectedSuccess', { platform: activeConnectingTool }),
            "success",
          );
        },
        onError: (message: string) => {
          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });
          showConnectionFeedbackToast(message, activeConnectingTool);
        },
        onRestriction: (restriction: any) => {
          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });
          applyEligibilityFromConnection(
            activeConnectingTool,
            restriction,
            submittedStoreUrl,
          );
        },
      };

      let result;
      if (option.provider === "shopify") {
        result = await workspaceIntegrations.connect(option.provider, {
          shop_domain: submittedShopifyStoreDomain,
          redirect_url: buildShopifyOnboardingConnectRedirectUrl(
            window.location.href,
            data.workspaceId,
          ),
          connection_scene: "onboarding",
        }, commonConnectOptions);
      } else if (option.provider === "bigcommerce") {
        result = await workspaceIntegrations.connect(option.provider, {
          store_domain: `https://store-${submittedStoreUrl}.mybigcommerce.com/`,
          store_hash: bigCommerceStoreHash.trim(),
          accountUuid: bigCommerceAccountUuid.trim(),
          connection_scene: "onboarding",
        }, commonConnectOptions);
      } else if (option.provider === "woocommerce") {
        result = await workspaceIntegrations.connect(option.provider, {
          store_domain: `https://${submittedStoreUrl}`,
          connection_scene: "onboarding",
        }, commonConnectOptions);
      } else if (option.provider === "magento") {
        result = await workspaceIntegrations.connect(option.provider, {
          store_domain: `https://${submittedStoreUrl}`,
          connection_scene: "onboarding",
        }, commonConnectOptions);
      } else {
        throw new Error(
          `Unsupported onboarding connection provider: ${option.provider}`,
        );
      }

      if (
        result?.success === false &&
        applyEligibilityFromConnection(
          connectingTool,
          result,
          submittedStoreUrl,
        )
      ) {
        resetConnectionForm();
        return;
      }

      if (result?.action === "REDIRECT" && result?.url) {
        window.location.href = result.url;
        return;
      }

      if (result?.uiOpened) {
        keepSyncingUntilCallback = true;
        resetConnectionForm();
        return;
      }

      const refreshedIntegration = await syncConnectedIntegrationMetadata(
        activeConnectingTool,
        option.provider,
      );
      const refreshedEligibilityPayload =
        buildEligibilityPayloadFromIntegration(refreshedIntegration);
      const eligibilityPayload = INTEGRATION_ELIGIBILITY_PROVIDERS.has(
        option.provider,
      )
        ? mergeEligibilityPayload(refreshedEligibilityPayload, result)
        : result;

      if (
        applyEligibilityFromConnection(
          activeConnectingTool,
          eligibilityPayload,
          submittedStoreUrl,
        )
      ) {
        resetConnectionForm();
        return;
      }
      if (shouldShowConnectionSuccessToast(result)) {
        showToast(
          t('website.onboarding.connect.connectedSuccess', { platform: activeConnectingTool }),
          "success",
        );
      }
      resetConnectionForm();
    } catch (error: any) {
      showConnectionFeedbackToast(error, activeConnectingTool);
    } finally {
      if (keepSyncingUntilCallback) {
        urlConnectionInFlightRef.current = false;
        return;
      }
      urlConnectionInFlightRef.current = false;
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(activeConnectingTool);
        return next;
      });
    }
  };

  const knownConnectedFromData = data.connectedIntegrations || [];
  const hasFreshConnectionSnapshot =
    workspaceIntegrations.hasFetchedCurrentWorkspace &&
    !workspaceIntegrations.error;
  const connectionRequirementsState = useMemo(
    () =>
      resolveOnboardingConnectionRequirements({
        serverIntegrations: workspaceIntegrations.allIntegrations,
        persistedConnectedToolNames: knownConnectedFromData,
        serverHydrated: Boolean(data.workspaceId) && hasFreshConnectionSnapshot,
      }),
    [
      data.workspaceId,
      hasFreshConnectionSnapshot,
      knownConnectedFromData,
      workspaceIntegrations.allIntegrations,
    ],
  );
  const connectedAppsDisplayState = resolveConnectedAppsDisplayState(
    connectionRequirementsState.connectedTools,
  );

  const hasConnectionOperationInFlight =
    syncingTools.size > 0 || Boolean(disconnectingPlatformId);
  const canAdvance = canContinueOnboardingConnectionStep({
    requirements: connectionRequirementsState,
    hasFreshServerSnapshot: hasFreshConnectionSnapshot,
    hasPendingAccountSelection: Boolean(
      googleAdsAccountSelectionConnectionId,
    ),
    hasConnectionOperationInFlight,
  });
  const showTrialUnavailableNotice =
    isTrialUnavailableNoticeVisible &&
    !connectingTool &&
    !isTrialUnavailableNoticeDismissed;

  const trialUnavailableNotice = showTrialUnavailableNotice ? (
    <RepeatedStoreNotice
      content={
        trialUnavailableNoticeContent || {
          title: buildFreeTrialUnavailableCopy(t).title,
          description: buildFreeTrialUnavailableCopy(t).description,
        }
      }
      onDismiss={dismissTrialUnavailableNotice}
    />
  ) : null;

  const closeTrialUnavailableModal = () => {
    setShowTrialUnavailableModal(false);
    setTrialUnavailableMessage(null);
    setTrialUnavailableWorkspaceName(null);
    setTrialUnavailablePlatformId(null);
  };

  const handleChooseAnotherStore = async () => {
    if (isTrialUnavailableContinuing) {
      return;
    }

    setIsTrialUnavailableContinuing(true);
    try {
      const platform = platformOptions.find(
        (option) => option.id === trialUnavailablePlatformId,
      );
      if (
        platform &&
        (workspaceIntegrations.isConnected(platform.provider) ||
          connectedIntegrationsRef.current.includes(platform.id))
      ) {
        const didDisconnect = await handleDisconnect(
          platform.id,
          platform.provider,
        );
        if (!didDisconnect) {
          return;
        }
      } else if (trialUnavailablePlatformId) {
        const nextTrialRequirements =
          removeConnectedIntegrationTrialRequirement(
            trialRequirementsRef.current,
            trialUnavailablePlatformId,
          );
        const connectedOptions = getConnectedOnboardingOptions(
          workspaceIntegrations.allIntegrations ?? [],
        );
        const firstConnectedStore = connectedOptions.find(
          (option) => option.categoryId === "store_ops",
        );
        const connectedStoreProfile = firstConnectedStore
          ? resolveConnectedStoreProfile(
              workspaceIntegrations.allIntegrations ?? [],
              firstConnectedStore.provider,
            )
          : { storeName: "", storeLogoUrl: null };
        trialRequirementsRef.current = nextTrialRequirements;
        updateData({
          connectedIntegrationTrialRequirements: nextTrialRequirements,
          trialEligibilityState:
            resolveAggregatedOnboardingTrialEligibilityState(
              nextTrialRequirements,
            ),
          connectedStoreName: connectedStoreProfile.storeName || "",
          connectedStoreLogoUrl: connectedStoreProfile.storeLogoUrl || null,
        });
      }
      closeTrialUnavailableModal();
    } finally {
      if (isStepConnectMountedRef.current) {
        setIsTrialUnavailableContinuing(false);
      }
    }
  };

  const handleContinueAfterTrialUnavailable = async () => {
    if (isTrialUnavailableContinuing) {
      return;
    }

    if (!canAdvance) {
      closeTrialUnavailableModal();
      return;
    }

    setIsTrialUnavailableContinuing(true);
    try {
      await onNext();
    } finally {
      if (isStepConnectMountedRef.current) {
        setIsTrialUnavailableContinuing(false);
      }
    }
  };

  const workspaceNameRaw = extractWorkspaceName(trialUnavailableMessage);
  const workspaceName =
    trialUnavailableWorkspaceName ||
    workspaceNameRaw ||
    t('website.onboarding.notice.freeTrialUnavailableWorkspaceFallback');
  const isManualCredentialConnecting = manualCredentialPlatformId
    ? syncingTools.has(manualCredentialPlatformId)
    : false;

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      {trialUnavailableNotice
        ? typeof document === "undefined"
          ? trialUnavailableNotice
          : createPortal(trialUnavailableNotice, document.body)
        : null}

      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-left">
          <div className="mb-8 flex w-full max-w-[860px] flex-col items-center text-center">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t('website.onboarding.connect.title')}
            </h2>
            <p className="mt-3 max-w-[860px] text-balance text-[15px] leading-relaxed text-[#666F8D] lg:text-[16px]">
              <span className="block lg:whitespace-nowrap">
                {t('website.onboarding.connect.description')}
              </span>
              <span className="block lg:whitespace-nowrap">
                {t('website.onboarding.connect.descriptionMore')}
              </span>
            </p>
            {/* Bustly Robot Assistant with Progress integrated inside Speech Bubble */}
            <div className="mt-8 flex w-full items-start justify-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-500">
              <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#272729] shadow-sm ring-2 ring-white/80">
                <img
                  src={BUSTLY_AVATAR_SRC}
                  alt="Bustly"
                  className="h-full w-full object-cover"
                />
              </div>
              <div
                role="status"
                aria-live="polite"
                className="relative max-w-[700px] rounded-[20px] border border-[#E0E3EC] bg-white px-5 py-3 text-left shadow-[0_12px_28px_rgba(26,22,47,0.06)]"
              >
                {/* Left pointing tail for speech bubble */}
                <div className="absolute left-[-6px] top-[18px] h-2.5 w-2.5 rotate-45 border-b border-l border-[#E0E3EC] bg-white" />
                <div className="flex flex-wrap items-center gap-2 text-[14px] leading-[1.35] sm:flex-nowrap sm:whitespace-nowrap">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className={clsx(
                      "absolute inline-flex h-full w-full animate-ping rounded-full opacity-70",
                      connectionRequirementsState.meetsConnectionCount ? "bg-[#52C41A]" : "bg-[#6E5BFF]"
                    )} />
                    <span className={clsx(
                      "relative inline-flex h-2 w-2 rounded-full",
                      connectionRequirementsState.meetsConnectionCount ? "bg-[#52C41A]" : "bg-[#1A162F]"
                    )} />
                  </span>
                  <span className="font-medium text-[#1A162F]">
                    {t("website.onboarding.connect.connectionProgress", {
                      connected: Math.min(
                        connectionRequirementsState.connectedCount,
                        connectionRequirementsState.requiredConnectionCount,
                      ),
                      required:
                        connectionRequirementsState.requiredConnectionCount,
                    })}
                  </span>
                  {connectedAppsDisplayState.visibleTools.length > 0 ? (
                    <div
                      aria-label={`${t("website.onboarding.connect.connectedAppsLabel")}: ${connectionRequirementsState.connectedTools.join(", ")}`}
                      className="ml-2 flex flex-wrap items-center gap-1.5 sm:flex-nowrap"
                    >
                      {connectedAppsDisplayState.visibleTools.map((tool) => (
                        <Tooltip key={tool} content={tool} side="top" sideOffset={6}>
                          <span
                            aria-label={tool}
                            data-onboarding-connected-app={tool}
                            role="img"
                            tabIndex={0}
                            className={clsx(
                              "flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] outline-none transition-transform duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 focus-visible:ring-offset-1",
                              getBrandIconFrameClassName(tool),
                            )}
                          >
                            <img
                              src={getBrandIcon(tool)}
                              alt=""
                              className={getBrandIconImageClassName(
                                tool,
                                "h-4 w-4",
                              )}
                            />
                          </span>
                        </Tooltip>
                      ))}
                      {connectedAppsDisplayState.overflowTools.length > 0 ? (
                        <Tooltip
                          content={(
                            <span className="block max-w-[420px] whitespace-normal leading-relaxed">
                              {connectedAppsDisplayState.overflowTools.join(", ")}
                            </span>
                          )}
                          contentClassName="max-w-[420px] whitespace-normal"
                          side="top"
                          sideOffset={6}
                        >
                          <span
                            aria-label={`${t("website.onboarding.connect.connectedAppsLabel")}: ${connectedAppsDisplayState.overflowTools.join(", ")}`}
                            data-onboarding-connected-app-overflow={connectedAppsDisplayState.overflowTools.length}
                            role="img"
                            tabIndex={0}
                            className="ml-0.5 flex h-7 min-w-7 shrink-0 items-center justify-center rounded-[8px] px-1 text-[12px] font-medium text-[#666F8D] outline-none transition-colors duration-200 hover:bg-[#F4F5F8] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 focus-visible:ring-offset-1"
                          >
                            +{connectedAppsDisplayState.overflowTools.length}
                          </span>
                        </Tooltip>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full max-w-[904px] flex-col items-center">
            {workspaceIntegrations.error ? (
              <div
                role="alert"
                className="mb-5 flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700"
              >
                <span>{t("website.onboarding.connect.statusLoadFailed")}</span>
                <button
                  type="button"
                  onClick={() => {
                    void workspaceIntegrations.refresh();
                  }}
                  className="rounded-full border border-red-200 bg-white px-4 py-2 text-[13px] font-bold text-red-700 transition-colors hover:bg-red-100"
                >
                  {t("common.retry")}
                </button>
              </div>
            ) : null}

            <div className="flex w-full flex-col gap-5 text-left md:flex-row md:items-start">
              <aside
                aria-label={t('website.integrationsSettings.integrations')}
                className="w-full shrink-0 md:w-[224px] md:border-r md:border-[#E4E7EF] md:pr-4"
              >
                <nav
                  aria-label={t('website.integrationsSettings.integrations')}
                  className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible"
                >
                  {ONBOARDING_BROWSE_CATEGORIES.map((category) => {
                    const isActive = selectedCategory === category.id;
                    const categoryLabel = t(ONBOARDING_CATEGORY_LABEL_KEYS[category.id]);
                    const categoryTools = connectionRequirementsState.categories[category.id].tools;
                    const categoryToolCount = categoryTools.length;

                    return (
                      <button
                        key={category.id}
                        type="button"
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => setSelectedCategory(category.id)}
                        className={clsx(
                          "flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal transition-colors md:w-full",
                          isActive
                            ? "bg-[#E6E7EA] text-[#1A162F]"
                            : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {categoryLabel}
                        </span>
                        <span className={clsx(
                          "text-xs font-normal tabular-nums",
                          isActive ? "text-[#666F8D]" : "text-[#8A93B2]"
                        )}>
                          {categoryToolCount}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              <div id="integrations-list" className="min-w-0 flex-1 flex flex-col gap-5 min-h-[280px] max-w-[660px]">
                {ONBOARDING_BROWSE_CATEGORIES.map((category) => {
                  const categoryTools = connectionRequirementsState.categories[category.id].tools;
                  const categoryConnectedTools = categoryTools.filter((tool) =>
                    connectionRequirementsState.connectedTools.includes(tool),
                  );
                  const categoryLabel = t(ONBOARDING_CATEGORY_LABEL_KEYS[category.id]);
                  const categoryDescription = t(
                    ONBOARDING_CATEGORY_DESCRIPTION_KEYS[category.id],
                  );

                  return (
                    <section
                      key={category.id}
                      data-onboarding-integration-category={category.id}
                      className={clsx(
                        "w-full",
                        selectedCategory === category.id ? "block" : "hidden"
                      )}
                    >

                      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                        {categoryTools.map((tool) => {
                          const platform = platformOptions.find(
                            (option) => option.id === tool,
                          );
                          if (!platform) {
                            return null;
                          }

                          return (
                            <StepConnectIntegrationRow
                              key={tool}
                              tool={tool}
                              isConnected={categoryConnectedTools.includes(tool)}
                              isSyncing={syncingTools.has(tool)}
                              onConnect={() => {
                                void handleConnect(platform.id, platform.provider);
                              }}
                              onDisconnect={() => {
                                setDisconnectingTool(tool);
                              }}
                            />
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex w-full flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={onBack}
                  className="flex min-w-[100px] items-center justify-center rounded-full border border-gray-200 bg-transparent px-6 py-3 text-[14px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-white hover:shadow"
                >
                  {t('website.onboarding.common.back')}
                </button>
              </div>
              <button
                onClick={() => {
                  void onNext();
                }}
                disabled={!canAdvance || isLoading || workspaceIntegrations.isLoading}
                className={clsx(
                  "flex items-center gap-2 rounded-full px-8 py-3 text-[15px] font-bold transition-all shadow-lg",
                  canAdvance && !isLoading && !workspaceIntegrations.isLoading
                    ? "bg-[#1A162F] text-white hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl"
                    : "cursor-not-allowed bg-gray-100 text-gray-400 shadow-none",
                )}
              >
                {isLoading || workspaceIntegrations.isLoading ? (
                  <CircleNotch
                    size={18}
                    weight="bold"
                    className="animate-spin"
                  />
                ) : null}
                {t('website.onboarding.common.continue')}
                {!isLoading && !workspaceIntegrations.isLoading ? (
                  <ArrowRight size={18} weight="bold" />
                ) : null}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!disconnectingTool}
        onClose={() => {
          if (disconnectingPlatformId === disconnectingTool) {
            return;
          }
          setDisconnectingTool(null);
        }}
        title={disconnectingTool ? "" : `Connect ${connectingTool}`}
        width="max-w-md"
        variant="reference"
        overlayClassName="z-[10000] bg-black/40 animate-in fade-in duration-200"
        containerClassName="shadow-2xl"
        headerClassName="border-b border-gray-100 px-6 pb-4 pt-5"
        titleClassName="font-semibold tracking-tight text-text-main"
        bodyClassName="px-6 pb-6 pt-4"
        showHeaderDivider
      >
        {disconnectingTool ? (
          <div className="space-y-4 pt-2">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
              <WarningCircle size={32} weight="fill" />
            </div>
            <h3 className="mb-2 text-center text-xl font-bold text-text-main">
              {t('website.onboarding.connect.disconnectTitle', { tool: disconnectingTool || '' })}
            </h3>
            <p className="mb-6 text-center leading-relaxed text-text-sub">
              {t('website.onboarding.connect.disconnectDescription')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDisconnectingTool(null)}
                disabled={disconnectingPlatformId === disconnectingTool}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 font-medium text-text-main transition-colors hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  const option = platformOptions.find(
                    (platform) => platform.id === disconnectingTool,
                  );
                  if (option) {
                    void handleDisconnect(option.id, option.provider);
                  }
                }}
                disabled={disconnectingPlatformId === disconnectingTool}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 font-bold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {disconnectingPlatformId === disconnectingTool
                  ? t('website.onboarding.connect.disconnecting')
                  : t('website.onboarding.connect.disconnect')}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <StoreConnectionModal
        isOpen={!!connectingTool && !!STORE_URL_INPUT_CONFIG[connectingTool]}
        platform={connectingTool}
        storeUrl={storeUrl}
        onStoreUrlChange={setStoreUrl}
        onClose={() => {
          resetConnectionForm();
        }}
        onSubmit={handleConnectionSubmit}
        submitLabel={t('website.onboarding.connect.connect')}
        isLoading={syncingTools.has(connectingTool || "")}
        submitDisabled={
          !storeUrl.trim() ||
          syncingTools.has(connectingTool || "") ||
          (connectingTool === "BigCommerce" &&
            (!bigCommerceStoreHash.trim() || !bigCommerceAccountUuid.trim()))
        }
        extraFields={
          connectingTool === "BigCommerce"
            ? [
                {
                  key: "store_hash",
                  label: t('website.onboarding.connect.storeHash'),
                  placeholder: t('website.onboarding.connect.storeHashPlaceholder'),
                  value: bigCommerceStoreHash,
                  onChange: setBigCommerceStoreHash,
                },
                {
                  key: "account_uuid",
                  label: t('website.onboarding.connect.accountUuid'),
                  placeholder: t('website.onboarding.connect.accountUuidPlaceholder'),
                  value: bigCommerceAccountUuid,
                  onChange: setBigCommerceAccountUuid,
                },
              ]
            : []
        }
      />

      <IntegrationApiKeyModal
        isOpen={!!manualCredentialPlatformId}
        onClose={() => {
          if (!isManualCredentialConnecting) {
            resetManualCredentialForm();
          }
        }}
        toolName={manualCredentialPlatformId || "PrestaShop"}
        apiKey={manualCredentialApiKey}
        onApiKeyChange={setManualCredentialApiKey}
        accountLabel={manualCredentialAccountLabel}
        onAccountLabelChange={setManualCredentialAccountLabel}
        storeUrl={manualCredentialStoreUrl}
        onStoreUrlChange={setManualCredentialStoreUrl}
        onConfirm={handleManualCredentialSubmit}
        isLoading={isManualCredentialConnecting}
      />

      <GoogleAdsAccountSelectionModal
        isOpen={Boolean(googleAdsAccountSelectionConnectionId)}
        connectionId={googleAdsAccountSelectionConnectionId}
        workspaceId={data.workspaceId}
        onClose={() => {
          void handleGoogleAdsAccountSelectionClose();
        }}
        onSaved={handleGoogleAdsAccountSelectionSaved}
      />

      <Modal
        isOpen={showTrialUnavailableModal}
        onClose={() => {
          void handleChooseAnotherStore();
        }}
        title={t('website.onboarding.notice.freeTrialUnavailableTitle')}
        hideHeader
        width="max-w-[480px]"
        closeOnOverlayClick={false}
      >
        <div className="flex flex-col items-start p-2 text-left">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-500">
              <WarningCircle size={24} weight="fill" />
            </div>
            <h3 className="text-[20px] font-bold text-[#1A162F]">
              {t('website.onboarding.notice.freeTrialUnavailableTitle')}
            </h3>
          </div>
          <p className="mb-6 whitespace-pre-line text-left text-[15px] leading-relaxed text-[#666F8D]">
            {t('website.onboarding.notice.freeTrialUnavailableDescription', {
              workspace_name: workspaceName,
            })}
          </p>
          <div className="flex w-full gap-3">
            <button
              type="button"
              onClick={() => {
                void handleChooseAnotherStore();
              }}
              disabled={isTrialUnavailableContinuing}
              className="flex-1 rounded-full border border-gray-200 bg-white py-3 text-[14px] font-bold text-[#1A162F] transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('website.onboarding.notice.freeTrialUnavailableCancel')}
            </button>
            <button
              type="button"
              onClick={handleContinueAfterTrialUnavailable}
              disabled={isTrialUnavailableContinuing}
              aria-busy={isTrialUnavailableContinuing}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#1A162F] py-3 text-[14px] font-bold text-white shadow-md transition-all hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-80"
            >
              {isTrialUnavailableContinuing ? (
                <CircleNotch size={16} weight="bold" className="animate-spin" />
              ) : null}
              {t(
                canAdvance
                  ? "website.onboarding.notice.freeTrialUnavailableContinue"
                  : "website.onboarding.notice.freeTrialUnavailableContinueSetup",
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const StepPlan: React.FC<{
  onBack?: () => void;
  data: any;
  updateData: (data: any) => void;
  onComplete?: () => void;
}> = ({ onBack, data, updateData, onComplete }) => {
  const { t, locale } = useTranslation();
  const {
    profile,
    setProfile,
    refreshWorkspaces,
    switchWorkspace,
    userBilling,
  } = useAuth();
  const { showToast } = useToast();
  const [plans, setPlans] = useState<Plan[]>([]);
  const trialWorkUnitsLabel = useMemo(() => {
    const value = new Intl.NumberFormat(locale).format(
      resolveOnboardingTrialWorkUnits(plans),
    );
    return t('website.onboarding.plan.trialWorkUnits', { value: value });
  }, [locale, plans, t]);
  const planOptions = useMemo(
    () => buildPlanOptions(t, plans, locale, trialWorkUnitsLabel),
    [locale, plans, t, trialWorkUnitsLabel],
  );
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [loadingPlanName, setLoadingPlanName] = useState<string | null>(null);
  const [pendingPlanOption, setPendingPlanOption] = useState<
    OnboardingPlanOption | null
  >(null);
  const [checkoutRequiredSelection, setCheckoutRequiredSelection] = useState<{
    planOptionId: string;
    targetPlan: Plan;
    paymentUrl?: string | null;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const response = await billingService.getPlans();
        if (!cancelled) {
          setPlans(response.filter((plan) => plan.isActive));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load onboarding plans", error);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPlans(false);
        }
      }
    };

    void loadPlans();

    return () => {
      cancelled = true;
    };
  }, []);

  const getMonthlyPlan = (planCode: string) =>
    plans.find(
      (plan) => plan.code === planCode && plan.billingCycle === "monthly",
    );

  const startCheckout = async (
    targetPlan: Plan,
    options?: { allowTrial?: boolean },
  ): Promise<OnboardingCheckoutNavigationState> => {
    const { successUrl, cancelUrl } = buildCheckoutReturnUrls({
      currentUrl: window.location.href,
      mode: "onboarding",
      planCode: targetPlan.code,
      planName: targetPlan.name,
      billingCycle: targetPlan.billingCycle,
      workspaceId: data.workspaceId,
    });

    const checkoutResponse = await billingService.createSubscription({
      planId: targetPlan.id,
      successUrl,
      cancelUrl,
      allowTrial: options?.allowTrial,
      axiosRequestConfig: { headers: { "X-Workspace-Id": data.workspaceId } },
    });

    if (checkoutResponse?.success && checkoutResponse?.paymentUrl) {
      window.location.href = checkoutResponse.paymentUrl;
      return "redirecting";
    }

    showToast(t('website.onboarding.plan.failedToStartCheckout'), "error");
    return "settled";
  };

  const handleSelectPlan = async (
    planOption: OnboardingPlanOption,
  ) => {
    if (loadingPlanName && loadingPlanName !== planOption.id) {
      return;
    }

    if (!data.workspaceId) {
      showToast(t('website.onboarding.plan.workspaceNotReady'), "error");
      return;
    }

    updateData({ plan: planOption.id });

    if (isLoadingPlans) {
      setPendingPlanOption(planOption);
      setLoadingPlanName(planOption.id);
      return;
    }

    setPendingPlanOption(null);

    const targetPlan = getMonthlyPlan(planOption.planCode);
    if (!targetPlan) {
      setLoadingPlanName(null);
      showToast(t('website.onboarding.plan.unavailableNow', { plan: planOption.title }), "error");
      return;
    }

    let checkoutNavigationState: OnboardingCheckoutNavigationState = "settled";

    try {
      if (data.trialEligibilityState === "checkout_required") {
        setLoadingPlanName(planOption.id);
        checkoutNavigationState = await startCheckout(targetPlan, {
          allowTrial: false,
        });
        return;
      }

      setLoadingPlanName(planOption.id);

      const checkoutUrls = buildCheckoutReturnUrls({
        currentUrl: window.location.href,
        mode: "onboarding",
        planCode: targetPlan.code,
        planName: targetPlan.name,
        billingCycle: targetPlan.billingCycle,
        workspaceId: data.workspaceId,
      });

      const response = await billingService.activateOnboardingTrial({
        planId: targetPlan.id,
        successUrl: checkoutUrls.successUrl,
        cancelUrl: checkoutUrls.cancelUrl,
        axiosRequestConfig: { headers: { "X-Workspace-Id": data.workspaceId } },
      });

      if (response?.activationMode === "setup_card_required") {
        if (response.paymentUrl) {
          window.location.href = response.paymentUrl;
          checkoutNavigationState = "redirecting";
          return;
        }
        throw new Error(
          response.message || t("website.onboarding.plan.failedToStartCheckout"),
        );
      }

      if (response?.activationMode === "checkout_required") {
        updateData({ trialEligibilityState: "checkout_required" });
        setCheckoutRequiredSelection({
          planOptionId: planOption.id,
          targetPlan,
          paymentUrl: response.paymentUrl,
        });
        return;
      }

      if (response?.workspaceId) {
        if (profile) {
          setProfile(
            buildOnboardingTrialProfile({
              profile,
              planId: targetPlan.id,
              planCode: targetPlan.code,
              planName: targetPlan.name,
              billingCycle: targetPlan.billingCycle as "monthly" | "yearly",
            }),
          );
        }

        await Promise.all([
          userBilling.refreshAll(),
          refreshWorkspaces({ silent: true }),
        ]);

        onComplete?.();
        navigate(
          buildOnboardingAdminSuccessRoute({
            workspaceId: data.workspaceId,
            planCode: targetPlan.code,
            planName: targetPlan.name,
          }),
          { replace: true },
        );
        return;
      }

      showToast(t('website.onboarding.plan.failedToStartTrial'), "error");
    } catch (error: any) {
      showToast(error?.message || t('website.onboarding.plan.failedToStartTrial'), "error");
    } finally {
      if (shouldReleaseOnboardingPlanLoading(checkoutNavigationState)) {
        setLoadingPlanName(null);
      }
    }
  };

  useEffect(() => {
    if (isLoadingPlans || !pendingPlanOption) {
      return;
    }

    void handleSelectPlan(pendingPlanOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPlans, pendingPlanOption, plans]);

  const handleConnectAnotherStoreFromCheckout = () => {
    if (loadingPlanName) {
      return;
    }

    setCheckoutRequiredSelection(null);
    onBack?.();
  };

  const handleConfirmedCheckout = async () => {
    if (!checkoutRequiredSelection || loadingPlanName) {
      return;
    }

    setLoadingPlanName(checkoutRequiredSelection.planOptionId);
    let checkoutNavigationState: OnboardingCheckoutNavigationState = "settled";

    try {
      if (checkoutRequiredSelection.paymentUrl) {
        window.location.href = checkoutRequiredSelection.paymentUrl;
        checkoutNavigationState = "redirecting";
        return;
      }

      checkoutNavigationState = await startCheckout(
        checkoutRequiredSelection.targetPlan,
        {
          allowTrial: false,
        },
      );
    } catch (error: any) {
      showToast(
        error?.message || t("website.onboarding.plan.failedToStartCheckout"),
        "error",
      );
    } finally {
      if (shouldReleaseOnboardingPlanLoading(checkoutNavigationState)) {
        setLoadingPlanName(null);
      }
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1140px] flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8 min-h-[calc(100vh-88px)]">
      <div className="fixed left-6 top-6 z-[101]">
        <img src={wordmark} alt="Bustly Logo" className="h-9 sm:h-10" />
      </div>

      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center">
          <div className="flex w-full max-w-[700px] items-center text-center mb-10 flex-col">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t(
                data.trialEligibilityState === "checkout_required"
                  ? "website.onboarding.plan.paidTitle"
                  : "website.onboarding.plan.title",
              )}
            </h2>
          </div>

          <div
            className="flex w-full max-w-[820px] items-center pt-0 flex-col"
          >
            <div className="grid w-full grid-cols-1 gap-5 text-left lg:grid-cols-2">
              {planOptions.map((plan) => {
                const isUnavailable =
                  !isLoadingPlans && !getMonthlyPlan(plan.planCode);
                const isAnotherPlanProcessing =
                  !!loadingPlanName && loadingPlanName !== plan.id;

                return (
                  <div key={plan.id} className="flex min-w-0 flex-col">
                    <PricingCard
                      plan={plan.pricingCard}
                      isOnboarding={true}
                      loadingPlanName={loadingPlanName ?? undefined}
                      disabled={
                        isAnotherPlanProcessing ||
                        isUnavailable
                      }
                      onAction={() => {
                        void handleSelectPlan(plan);
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <p className="mt-3 w-full text-center text-[12px] leading-relaxed text-[#666F8D]">
              {t('website.onboarding.plan.renewalNote')}
            </p>

            <div className="mt-10 flex w-full flex-wrap items-center justify-start gap-3">
              <button
                onClick={onBack}
                className="flex min-w-[116px] items-center justify-center rounded-full border border-gray-200 bg-transparent px-7 py-3.5 text-[15px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-white hover:shadow"
              >
                {t('website.onboarding.common.back')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={Boolean(checkoutRequiredSelection)}
        onClose={handleConnectAnotherStoreFromCheckout}
        title={t("website.onboarding.plan.checkoutRequired.title")}
        hideHeader
        width="max-w-[480px]"
        closeOnOverlayClick={false}
      >
        <div className="flex flex-col items-start p-2 text-left">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-500">
              <WarningCircle size={24} weight="fill" />
            </div>
            <h3 className="text-[20px] font-bold text-[#1A162F]">
              {t("website.onboarding.plan.checkoutRequired.title")}
            </h3>
          </div>
          <p className="mb-6 text-[15px] leading-relaxed text-[#666F8D]">
            {t("website.onboarding.plan.checkoutRequired.description")}
          </p>
          <div className="flex w-full gap-3">
            <button
              type="button"
              onClick={handleConnectAnotherStoreFromCheckout}
              disabled={Boolean(loadingPlanName)}
              className="flex-1 rounded-full border border-gray-200 bg-white py-3 text-[14px] font-bold text-[#1A162F] transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("website.onboarding.plan.checkoutRequired.connectAnotherStore")}
            </button>
            <button
              type="button"
              onClick={() => {
                void handleConfirmedCheckout();
              }}
              disabled={Boolean(loadingPlanName)}
              aria-busy={Boolean(loadingPlanName)}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#1A162F] py-3 text-[14px] font-bold text-white shadow-md transition-all hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-80"
            >
              {loadingPlanName ? (
                <CircleNotch size={16} weight="bold" className="animate-spin" />
              ) : null}
              {t("website.onboarding.plan.checkoutRequired.continueToCheckout")}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

const OnboardingOverlay: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    nextStep,
    prevStep,
    stopOnboarding,
    onboardingData,
    updateOnboardingData,
    isAcceptingInvite,
    setIsAcceptingInvite,
  } = useOnboarding();
  const {
    user,
    profile,
    workspaceMembers,
    logout,
    workspaces,
    refreshWorkspaces,
  } = useAuth();
  const { invitations, isLoadingInvitations, refreshInvitations } = workspaceMembers;
  const currentStep = onboardingData.currentStep ?? 0;
  const workspaceIntegrations = useWorkspaceIntegrations(
    onboardingData.workspaceId,
    {
      enabled: Boolean(onboardingData.workspaceId),
      requireFreshFetch: true,
    },
  );
  const [isTrialUnavailableNoticeVisible, setIsTrialUnavailableNoticeVisible] =
    useState(false);
  const [isTrialUnavailableNoticeDismissed, setIsTrialUnavailableNoticeDismissed] = useState(false);
  const [trialUnavailableNoticeContent, setTrialUnavailableNoticeContent] =
    useState<RepeatedStoreNoticeContent | null>(null);
  const [userChoseCreate, setUserChoseCreate] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<InviteStatusResponse | null>(
    null,
  );
  const [isLoadingInviteStatus, setIsLoadingInviteStatus] = useState(false);
  const [inviteStatusError, setInviteStatusError] = useState<string | null>(
    null,
  );
  const [hasVerifiedOnboardingInvitations, setHasVerifiedOnboardingInvitations] =
    useState(false);
  const [displayStep, setDisplayStep] = useState(currentStep);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPreparingWorkspace, setIsPreparingWorkspace] = useState(false);
  const [prepareWorkspaceError, setPrepareWorkspaceError] = useState<
    string | null
  >(null);
  const [isFinalizingWorkspace, setIsFinalizingWorkspace] = useState(false);
  const [finalizeWorkspaceError, setFinalizeWorkspaceError] = useState<
    string | null
  >(null);
  const [pendingIntentSubmission, setPendingIntentSubmission] = useState<{
    intent?: string;
    otherIntent?: string;
  } | null>(null);
  const onboardingScrollContainerRef = useRef<HTMLDivElement>(null);
  const intentSubmissionLockRef = useRef(false);
  const autoAcceptInviteKeyRef = useRef<string | null>(null);
  const acceptedInviteWorkspaceKeyRef = useRef<string | null>(null);
  const inviteToken = searchParams.get("invite_token")?.trim() || "";
  const normalizedCurrentEmail = resolveCurrentInviteEmail(
    user?.email,
    profile?.userInfo?.email,
  );
  const shouldSkipStoreConnectStep = useMemo(
    () =>
      shouldAllowOnboardingStoreConnectSkip({
        workspaceSettings: onboardingData.workspaceSettings,
      }),
    [onboardingData.workspaceSettings],
  );
  const visibleOnboardingSteps = useMemo(
    () => getVisibleOnboardingSteps(shouldSkipStoreConnectStep),
    [shouldSkipStoreConnectStep],
  );
  const hasFreshConnectionSnapshot =
    workspaceIntegrations.hasFetchedCurrentWorkspace &&
    !workspaceIntegrations.error;
  const planEntryConnectionRequirements = useMemo(
    () =>
      resolveOnboardingConnectionRequirements({
        serverIntegrations: workspaceIntegrations.allIntegrations,
        persistedConnectedToolNames:
          onboardingData.connectedIntegrations || [],
        serverHydrated:
          Boolean(onboardingData.workspaceId) && hasFreshConnectionSnapshot,
      }),
    [
      hasFreshConnectionSnapshot,
      onboardingData.connectedIntegrations,
      onboardingData.workspaceId,
      workspaceIntegrations.allIntegrations,
    ],
  );
  const planStepAccessState = resolveOnboardingPlanStepAccess({
    requirements: planEntryConnectionRequirements,
    hasFreshServerSnapshot: hasFreshConnectionSnapshot,
    shouldSkipConnectionStep: shouldSkipStoreConnectStep,
  });
  const hasPlanStepRestoreError =
    currentStep === 2 &&
    !shouldSkipStoreConnectStep &&
    Boolean(workspaceIntegrations.error);
  const isRestoringPlanStep =
    currentStep === 2 &&
    planStepAccessState === "checking-connections" &&
    !hasPlanStepRestoreError;
  const shouldShowConnectStepForPlanAccess =
    currentStep === 2 &&
    planStepAccessState === "blocked";
  const stepForRendering = resolveOnboardingStepForRendering({
    displayStep,
    planStepAccessState,
  });
  const guardedCurrentStep = shouldShowConnectStepForPlanAccess
    ? 1
    : currentStep;
  const visibleCurrentStepIndex = getVisibleOnboardingStepIndex(
    guardedCurrentStep,
    shouldSkipStoreConnectStep,
  );

  useWorkspacePolling();

  useEffect(() => {
    if (currentStep !== 2 || planStepAccessState !== "blocked") {
      return;
    }

    updateOnboardingData({ currentStep: 1 });
  }, [currentStep, planStepAccessState, updateOnboardingData]);

  const isNewUser = !workspaces || workspaces.length === 0;
  const dismissTrialUnavailableNotice = useCallback(() => {
    setIsTrialUnavailableNoticeDismissed(true);
    setIsTrialUnavailableNoticeVisible(false);
  }, []);
  const triggerTrialUnavailableNotice = useCallback((
    content?: RepeatedStoreNoticeContent | null,
  ) => {
    setTrialUnavailableNoticeContent(
      content || buildFreeTrialUnavailableCopy(t),
    );
    setIsTrialUnavailableNoticeDismissed(false);
    setIsTrialUnavailableNoticeVisible(true);
  }, []);
  const showTopRightTrialUnavailableNotice =
    currentStep === 1 &&
    isTrialUnavailableNoticeVisible &&
    !isTrialUnavailableNoticeDismissed;

  useEffect(() => {
    if (!shouldSkipStoreConnectStep || currentStep !== 1) {
      return;
    }

    updateOnboardingData({ currentStep: 2 });
  }, [currentStep, shouldSkipStoreConnectStep, updateOnboardingData]);

  useEffect(() => {
    if (currentStep !== 1) {
      setIsTrialUnavailableNoticeVisible(false);
      setTrialUnavailableNoticeContent(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!showTopRightTrialUnavailableNotice) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      dismissTrialUnavailableNotice();
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [dismissTrialUnavailableNotice, showTopRightTrialUnavailableNotice]);

  useEffect(() => {
    let isCancelled = false;

    if (!inviteToken) {
      setInviteStatus(null);
      setInviteStatusError(null);
      setIsLoadingInviteStatus(false);
      return;
    }

    const loadInviteStatus = async () => {
      setIsLoadingInviteStatus(true);
      setInviteStatusError(null);

      try {
        const response = await featureService.getInviteStatus(inviteToken);

        if (!isCancelled) {
          setInviteStatus(response);
        }
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatus(null);
          setInviteStatusError(
            error?.message || "We could not load this invitation.",
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingInviteStatus(false);
        }
      }
    };

    void loadInviteStatus();

    return () => {
      isCancelled = true;
    };
  }, [inviteToken]);

  useEffect(() => {
    let isCancelled = false;

    if (inviteToken) {
      setHasVerifiedOnboardingInvitations(true);
      return undefined;
    }

    setHasVerifiedOnboardingInvitations(false);

    const verifyOnboardingInvitations = async () => {
      await refreshInvitations();
      if (!isCancelled) {
        setHasVerifiedOnboardingInvitations(true);
      }
    };

    void verifyOnboardingInvitations();

    return () => {
      isCancelled = true;
    };
  }, [inviteToken, refreshInvitations]);

  useEffect(() => {
    if (currentStep !== displayStep) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayStep(currentStep);
        if (onboardingScrollContainerRef.current) {
          onboardingScrollContainerRef.current.scrollTop = 0;
        }
        requestAnimationFrame(() => {
          setIsTransitioning(false);
        });
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStep, displayStep]);

  const targetedInvitation = useMemo<WorkspaceInvitation | null>(() => {
    if (!inviteToken || !inviteStatus?.valid) {
      return null;
    }

    const matchedInvitation =
      invitations.find((invitation) => invitation.token === inviteToken) ||
      invitations.find(
        (invitation) =>
          invitation.workspaceId === inviteStatus.workspaceId &&
          normalizeEmail(invitation.email) ===
            normalizeEmail(inviteStatus.invitedEmail),
      );

    if (matchedInvitation) {
      return matchedInvitation;
    }

    return {
      id: `invite-token:${inviteToken}`,
      workspaceId: inviteStatus.workspaceId,
      workspaceName: inviteStatus.workspaceName,
      email: inviteStatus.invitedEmail,
      role: inviteStatus.role,
      dataRoleName:
        inviteStatus.dataRoleName ||
        inviteStatus.data_role_name ||
        inviteStatus.workspaceRoleName ||
        inviteStatus.workspaceRoleKey ||
        null,
      status: "PENDING",
      invitedBy:
        inviteStatus.inviterName || inviteStatus.inviterEmail || "A teammate",
      inviterEmail: inviteStatus.inviterEmail,
      inviterName: inviteStatus.inviterName,
      token: inviteToken,
      expiresAt: inviteStatus.expiresAt,
      createdAt: new Date().toISOString(),
    } as unknown as WorkspaceInvitation;
  }, [inviteStatus, inviteToken, invitations]);

  const hasInviteEmailMismatch = Boolean(
    inviteToken &&
      inviteStatus?.valid &&
      inviteStatus.invitedEmail &&
      normalizedCurrentEmail &&
      normalizeEmail(inviteStatus.invitedEmail) !== normalizedCurrentEmail,
  );
  const inviteEmailMatchesUser = !hasInviteEmailMismatch;
  const shouldProcessInviteToken = shouldProcessInviteTokenOnOnboarding({
    inviteToken,
    inviteValid: inviteStatus?.valid,
    inviteStatus: inviteStatus?.status,
    invitedEmail: inviteStatus?.invitedEmail,
    currentEmail: normalizedCurrentEmail,
    userChoseCreate,
    hasInviteStatusError: Boolean(inviteStatusError),
  });
  const showTargetedInviteSelection = Boolean(
    inviteToken &&
    inviteStatus?.valid &&
    inviteEmailMatchesUser &&
    !userChoseCreate &&
    !shouldProcessInviteToken &&
    targetedInvitation,
  );

  useEffect(() => {
    const shouldAutoAccept = shouldAutoAcceptInviteOnOnboarding({
      inviteToken,
      inviteValid: inviteStatus?.valid,
      inviteStatus: inviteStatus?.status,
      invitedEmail: inviteStatus?.invitedEmail,
      currentEmail: normalizedCurrentEmail,
      isAcceptingInvite,
      userChoseCreate,
    });

    if (!shouldAutoAccept) {
      return undefined;
    }

    const autoAcceptKey = `${inviteToken}:${normalizedCurrentEmail}`;
    if (autoAcceptInviteKeyRef.current === autoAcceptKey) {
      return undefined;
    }

    autoAcceptInviteKeyRef.current = autoAcceptKey;
    let isCancelled = false;

    const acceptInviteAfterAuthCallback = async () => {
      setIsAcceptingInvite(true);
      setInviteStatusError(null);

      try {
        const response = await featureService.acceptInvitation(inviteToken);
        const workspaceId = resolveInviteAcceptWorkspaceId(response);

        if (!workspaceId) {
          throw new Error("We could not accept this invitation.");
        }

        if (isCancelled) {
          return;
        }

        stopOnboarding();
        window.location.assign(buildInviteSuccessAdminRoute(workspaceId));
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatusError(
            error?.message || "We could not accept this invitation.",
          );
          setIsAcceptingInvite(false);
        }
      }
    };

    void acceptInviteAfterAuthCallback();

    return () => {
      isCancelled = true;
    };
  }, [
    inviteStatus?.invitedEmail,
    inviteStatus?.valid,
    inviteToken,
    normalizedCurrentEmail,
    setIsAcceptingInvite,
    stopOnboarding,
    userChoseCreate,
  ]);

  useEffect(() => {
    const workspaceId = inviteStatus?.workspaceId;
    const shouldEnterAcceptedWorkspace = shouldEnterAcceptedInviteWorkspaceOnOnboarding({
      inviteToken,
      inviteValid: inviteStatus?.valid,
      inviteStatus: inviteStatus?.status,
      workspaceId,
      invitedEmail: inviteStatus?.invitedEmail,
      currentEmail: normalizedCurrentEmail,
      isAcceptingInvite,
      userChoseCreate,
    });

    if (!shouldEnterAcceptedWorkspace || !workspaceId) {
      return undefined;
    }

    const acceptedInviteKey = `${inviteToken}:${workspaceId}:${normalizedCurrentEmail}`;
    if (acceptedInviteWorkspaceKeyRef.current === acceptedInviteKey) {
      return undefined;
    }

    acceptedInviteWorkspaceKeyRef.current = acceptedInviteKey;
    let isCancelled = false;

    const enterAcceptedInviteWorkspace = async () => {
      setIsAcceptingInvite(true);
      setInviteStatusError(null);

      try {
        if (isCancelled) {
          return;
        }

        stopOnboarding();
        window.location.assign(buildInviteSuccessAdminRoute(workspaceId));
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatusError(
            error?.message || "We could not open this workspace.",
          );
          setIsAcceptingInvite(false);
        }
      }
    };

    void enterAcceptedInviteWorkspace();

    return () => {
      isCancelled = true;
    };
  }, [
    inviteStatus?.invitedEmail,
    inviteStatus?.status,
    inviteStatus?.valid,
    inviteStatus?.workspaceId,
    inviteToken,
    normalizedCurrentEmail,
    setIsAcceptingInvite,
    stopOnboarding,
    userChoseCreate,
  ]);
  const pendingConnectionRequest =
    onboardingData.pendingConnectionRequest ?? null;
  const setPendingConnectionRequest = useCallback<
    React.Dispatch<React.SetStateAction<PendingConnectionRequest | null>>
  >(
    (nextPendingConnectionRequest) => {
      const resolvedPendingConnectionRequest =
        typeof nextPendingConnectionRequest === "function"
          ? nextPendingConnectionRequest(pendingConnectionRequest)
          : nextPendingConnectionRequest;

      updateOnboardingData({ pendingConnectionRequest: resolvedPendingConnectionRequest });
    },
    [pendingConnectionRequest, updateOnboardingData],
  );
  const isCreatingOrConnectingWorkspace = Boolean(
    userChoseCreate || pendingConnectionRequest,
  );
  const showDefaultInviteSelection =
    hasVerifiedOnboardingInvitations &&
    !isLoadingInvitations &&
    !isCreatingOrConnectingWorkspace &&
    !inviteToken &&
    invitations &&
    invitations.length > 0 &&
    !userChoseCreate;
  const showInviteSelection =
    showTargetedInviteSelection || showDefaultInviteSelection;
  const isWorkspaceReadyForConnect = Boolean(
    onboardingData.workspaceId &&
      !isPreparingWorkspace &&
      !prepareWorkspaceError
  );

  const prepareWorkspaceForConnectStep = useCallback(async () => {
    if (
      hasReusableOnboardingWorkspace(onboardingData.workspaceId, workspaces)
    ) {
      return (
        workspaces?.find((workspace) => workspace?.id === onboardingData.workspaceId) ||
        null
      );
    }

    if (isPreparingWorkspace) {
      return null;
    }

    setIsPreparingWorkspace(true);
    setPrepareWorkspaceError(null);

    try {
      if (onboardingData.workspaceId) {
        updateOnboardingData({
          workspaceId: undefined,
          businessName: "",
        });
      }

      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const workspacePayload = buildInitialOnboardingWorkspacePayload({
        timezone,
      });
      const workspace = await featureService.createWorkspace(workspacePayload);

      updateOnboardingData({
        workspaceId: workspace.id,
        businessName: workspace.name,
        logoUrl: workspace.logoUrl || null,
        connectedStoreName: "",
        connectedStoreLogoUrl: null,
        connectedIntegrations: [],
        connectedIntegrationTrialRequirements: {},
        trialEligibilityState: null,
        workspaceSettings: workspace.settings || null,
      });
      return workspace;
    } catch (error: any) {
      setPrepareWorkspaceError(
        error?.message || "We could not prepare your workspace.",
      );
      return null;
    } finally {
      setIsPreparingWorkspace(false);
    }
  }, [
    isPreparingWorkspace,
    onboardingData.workspaceId,
    updateOnboardingData,
    workspaces,
  ]);

  useEffect(() => {
    if (currentStep < 1 || showInviteSelection || shouldProcessInviteToken) {
      return;
    }

    if (
      hasReusableOnboardingWorkspace(onboardingData.workspaceId, workspaces)
    ) {
      return;
    }

    if (
      onboardingData.workspaceId ||
      isPreparingWorkspace ||
      prepareWorkspaceError
    ) {
      return;
    }

    void prepareWorkspaceForConnectStep();
  }, [
    currentStep,
    onboardingData.workspaceId,
    isPreparingWorkspace,
    prepareWorkspaceError,
    prepareWorkspaceForConnectStep,
    shouldProcessInviteToken,
    showInviteSelection,
    workspaces,
  ]);

  const prepareWorkspaceOnWelcome = async () => {
    setUserChoseCreate(true);
    setPrepareWorkspaceError(null);

    const workspace = await prepareWorkspaceForConnectStep();
    if (!workspace) {
      return;
    }

    if (hasOnboardingStoreConnectSkipTrace(workspace.settings)) {
      updateOnboardingData({ currentStep: 2 });
      return;
    }

    nextStep();
  };

  const goBackFromPlanStep = useCallback(() => {
    if (shouldSkipStoreConnectStep) {
      updateOnboardingData({ currentStep: 0 });
      return;
    }

    prevStep();
  }, [prevStep, shouldSkipStoreConnectStep, updateOnboardingData]);

  const handleConnectNext = useCallback(async () => {
    if (intentSubmissionLockRef.current) {
      return;
    }

    intentSubmissionLockRef.current = true;
    setIsFinalizingWorkspace(true);
    setFinalizeWorkspaceError(null);

    try {
      const payload = buildOnboardingWorkspaceUpdatePayload({
        storeName:
          onboardingData.connectedStoreName || onboardingData.businessName,
        storeLogoUrl:
          onboardingData.connectedStoreLogoUrl ||
          onboardingData.logoUrl ||
          null,
        currentSettings: onboardingData.workspaceSettings,
        intent: "",
        otherIntent: "",
      });
      const workspace = await featureService.updateWorkspace(
        onboardingData.workspaceId!,
        payload,
      );

      updateOnboardingData({
        businessName: workspace.name,
        logoUrl: workspace.logoUrl || null,
        workspaceSettings: workspace.settings || null,
      });
      nextStep();
      void refreshWorkspaces({
        silent: true,
        preferredWorkspaceId: onboardingData.workspaceId,
      }).catch((refreshError) => {
        console.error(
          "[Onboarding] Failed to refresh workspaces after connect submit",
          refreshError,
        );
      });
    } catch (error: any) {
      setFinalizeWorkspaceError(
        error?.message || "We could not finish setting up your workspace.",
      );
    } finally {
      intentSubmissionLockRef.current = false;
      setIsFinalizingWorkspace(false);
    }
  }, [
    nextStep,
    onboardingData.businessName,
    onboardingData.connectedStoreLogoUrl,
    onboardingData.connectedStoreName,
    onboardingData.logoUrl,
    onboardingData.workspaceId,
    onboardingData.workspaceSettings,
    refreshWorkspaces,
    updateOnboardingData,
  ]);

  const handleComplete = () => {
    stopOnboarding();
  };

  const steps = [
    <StepWelcome
      key="welcome"
      onNext={prepareWorkspaceOnWelcome}
      error={prepareWorkspaceError}
      isLoading={isPreparingWorkspace}
    />,
    <StepConnect
      key="connect"
      onNext={handleConnectNext}
      onBack={prevStep}
      isLoading={isFinalizingWorkspace}
      data={onboardingData}
      updateData={updateOnboardingData}
      isTrialUnavailableNoticeVisible={isTrialUnavailableNoticeVisible}
      isTrialUnavailableNoticeDismissed={isTrialUnavailableNoticeDismissed}
      trialUnavailableNoticeContent={trialUnavailableNoticeContent}
      dismissTrialUnavailableNotice={dismissTrialUnavailableNotice}
      triggerTrialUnavailableNotice={triggerTrialUnavailableNotice}
      pendingConnectionRequest={pendingConnectionRequest}
      setPendingConnectionRequest={setPendingConnectionRequest}
      workspaceReady={isWorkspaceReadyForConnect}
      workspaceError={prepareWorkspaceError}
      workspaceIntegrations={workspaceIntegrations}
    />,
    <StepPlan
      key="plan"
      onBack={goBackFromPlanStep}
      data={onboardingData}
      updateData={updateOnboardingData}
      onComplete={handleComplete}
    />,
  ];
  return (
    <div
      ref={onboardingScrollContainerRef}
      className={clsx(
        "fixed inset-0 z-[100] overflow-x-hidden overflow-y-auto transition-colors duration-500",
        "bg-[#F8F9FB]",
      )}
    >
      {!showInviteSelection && !shouldProcessInviteToken ? (
        <div className="fixed top-0 left-0 w-full h-1 bg-gray-100 z-[101] transition-opacity duration-300">
          <div
            className="h-full bg-text-main transition-all duration-500 ease-out"
            style={{
              width: `${((visibleCurrentStepIndex + 1) / visibleOnboardingSteps.length) * 100}%`,
            }}
          />
        </div>
      ) : null}

      {!showTopRightTrialUnavailableNotice ? (
        <div className="fixed top-6 right-6 z-[101] flex items-center gap-3">
          {isNewUser ? (
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all"
            >
              <SignOut size={16} className="text-[#1A162F]" weight="bold" />
              <span className="text-[13px] font-bold text-[#1A162F]">{t('website.onboarding.common.logOut')}</span>
            </button>
          ) : (
            <button
              onClick={() => {
                stopOnboarding();
                navigate("/");
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all"
            >
              <X size={16} className="text-[#1A162F]" weight="bold" />
              <span className="text-[13px] font-bold text-[#1A162F]">{t('website.onboarding.common.close')}</span>
            </button>
          )}
        </div>
      ) : null}

      <div
        className={clsx(
          "min-h-screen flex flex-col pb-12 pt-6 transition-all duration-500 ease-out",
        )}
      >
        <div className="relative flex flex-1 w-full flex-col items-center justify-center">
          {isLoadingInviteStatus ? (
            <div className="flex flex-col items-center gap-3 text-text-sub">
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t('website.onboarding.invite.checkingInvitation')}
              </p>
            </div>
          ) : inviteToken && (!inviteStatus?.valid || inviteStatusError) ? (
            <InviteStateCard
              icon={<WarningCircle size={24} weight="bold" />}
              title={t('website.onboarding.invite.invalidTitle')}
              description={
                <>
                  <p>
                    {inviteStatusError ||
                      t('website.onboarding.invite.invalidDescription')}
                  </p>
                  {inviteStatus ? (
                    <p>{buildExpiryText(inviteStatus.daysUntilExpiry, t)}</p>
                  ) : null}
                </>
              }
              action={
                <button
                  onClick={() => navigate("/")}
                  className="rounded-xl bg-text-main px-6 py-3 font-semibold text-white transition-colors hover:bg-text-main/90"
                >
                  {t('website.onboarding.invite.goToDashboard')}
                </button>
              }
            />
          ) : shouldProcessInviteToken ? (
            <div className="flex flex-col items-center gap-3 text-text-sub">
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t('website.onboarding.invite.joining')}
              </p>
            </div>
          ) : inviteToken && inviteStatus?.valid && !inviteEmailMatchesUser ? (
            <InviteDifferentAccountCard
              inviteStatus={inviteStatus}
              currentEmail={normalizedCurrentEmail || null}
              onSwitchAccount={logout}
            />
          ) : showTargetedInviteSelection && targetedInvitation ? (
            <InviteSelection
              invitations={[targetedInvitation]}
              onChooseCreate={() => setUserChoseCreate(true)}
              onAcceptingChange={setIsAcceptingInvite}
            />
          ) : showDefaultInviteSelection ? (
            <InviteSelection
              invitations={invitations}
              onChooseCreate={() => setUserChoseCreate(true)}
              onAcceptingChange={setIsAcceptingInvite}
            />
          ) : hasPlanStepRestoreError ? (
            <div
              role="alert"
              className="flex max-w-[560px] flex-col items-center gap-4 rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-center text-[14px] text-red-700"
            >
              <span>{t("website.onboarding.connect.statusLoadFailed")}</span>
              <button
                type="button"
                onClick={() => {
                  void workspaceIntegrations.refresh();
                }}
                className="rounded-full border border-red-200 bg-white px-4 py-2 text-[13px] font-bold text-red-700 transition-colors hover:bg-red-100"
              >
                {t("common.retry")}
              </button>
            </div>
          ) : isRestoringPlanStep ? (
            <div
              className="flex flex-col items-center gap-3 text-text-sub"
              role="status"
              aria-live="polite"
            >
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t("website.onboarding.plan.loadingOptions")}
              </p>
            </div>
          ) : (
            <div
              className={clsx(
                "flex-1 flex flex-col w-full transition-all duration-300 ease-in-out will-change-[opacity,transform]",
                isTransitioning
                  ? "opacity-0 translate-y-4"
                  : "opacity-100 translate-y-0",
              )}
            >
              {steps[stepForRendering]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingOverlay;
